import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { paymentService, studentService, batchService, formatRwf, getTrainingFee, SECONDARY_TERM_FEE } from '../services';
import { computePaymentSummary, computeAllBalances, computeBatchPaymentStats } from '../utils/paymentSummary';
import { generatePaymentInvoicePdf } from '../utils/paymentInvoicePdf';
import { getProgramLabel, getBatchLabel, getStudentBatchId, formatBatchDates } from '../utils/studentDisplay';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const BALANCE_STATUS_FILTERS = [
  { value: 'outstanding', label: 'Outstanding only' },
  { value: '', label: 'All students' },
  { value: 'partial', label: 'Partial' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Fully paid' },
];

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [viewPayment, setViewPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionMenuPayment, setActionMenuPayment] = useState(null);
  const [generatingReceiptId, setGeneratingReceiptId] = useState(null);
  const [editPayment, setEditPayment] = useState(null);
  const [editFormData, setEditFormData] = useState({ paymentProof: '', receiptPath: '', officeNote: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [formData, setFormData] = useState({
    student: '', method: 'office', amount: '', receiptPath: '', paymentProof: '', officeNote: '',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [balances, setBalances] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [listView, setListView] = useState('balances');
  const [balanceStatusFilter, setBalanceStatusFilter] = useState('outstanding');
  const [balancePage, setBalancePage] = useState(1);
  const [balancePageSize, setBalancePageSize] = useState(10);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState('');
  const [groupHistoryByBatch, setGroupHistoryByBatch] = useState(false);
  const [studentProofs, setStudentProofs] = useState(null);
  const [studentProofsLoading, setStudentProofsLoading] = useState(false);

  const matchesBatchFilter = useCallback((student) => {
    if (!batchFilter) return true;
    if (batchFilter === '__none__') return !getStudentBatchId(student);
    return getStudentBatchId(student) === batchFilter;
  }, [batchFilter]);

  const balanceByStudentId = useMemo(() => {
    const map = new Map();
    balances.forEach((b) => map.set(String(b.studentId), b));
    return map;
  }, [balances]);

  const balanceTableRows = useMemo(() => (
    students
      .map((student) => ({
        student,
        balance: balanceByStudentId.get(String(student._id)) || computePaymentSummary(student, payments),
      }))
      .sort((a, b) => {
        const rank = { unpaid: 0, partial: 1, paid: 2 };
        const diff = (rank[a.balance.status] ?? 1) - (rank[b.balance.status] ?? 1);
        if (diff !== 0) return diff;
        return (b.balance.balance || 0) - (a.balance.balance || 0);
      })
  ), [students, balanceByStudentId, payments]);

  const filteredBalanceRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return balanceTableRows.filter(({ student, balance }) => {
      if (!matchesBatchFilter(student)) return false;
      if (balanceStatusFilter === 'outstanding' && balance.status === 'paid') return false;
      if (balanceStatusFilter && balanceStatusFilter !== 'outstanding' && balance.status !== balanceStatusFilter) {
        return false;
      }
      if (!term) return true;
      return (
        (student.fullName || '').toLowerCase().includes(term) ||
        (student.email || '').toLowerCase().includes(term) ||
        (student.phone || '').toLowerCase().includes(term) ||
        getProgramLabel(student).toLowerCase().includes(term) ||
        getBatchLabel(student).toLowerCase().includes(term)
      );
    });
  }, [balanceTableRows, balanceStatusFilter, searchTerm, matchesBatchFilter]);

  const balanceTotalPages = Math.max(1, Math.ceil(filteredBalanceRows.length / balancePageSize));
  const safeBalancePage = Math.min(balancePage, balanceTotalPages);
  const balanceRangeStart = filteredBalanceRows.length === 0 ? 0 : (safeBalancePage - 1) * balancePageSize + 1;
  const balanceRangeEnd = Math.min(safeBalancePage * balancePageSize, filteredBalanceRows.length);
  const paginatedBalanceRows = useMemo(() => {
    const start = (safeBalancePage - 1) * balancePageSize;
    return filteredBalanceRows.slice(start, start + balancePageSize);
  }, [filteredBalanceRows, safeBalancePage, balancePageSize]);

  useEffect(() => { setBalancePage(1); }, [searchTerm, balanceStatusFilter, balancePageSize, listView, batchFilter]);
  useEffect(() => { setHistoryPage(1); }, [searchTerm, historyPageSize, listView, batchFilter]);

  useEffect(() => { fetchPayments(); fetchStudents(); fetchBatches(); }, []);

  const fetchBatches = async () => {
    try {
      const data = await batchService.getAllBatches();
      setBatches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading batches:', err);
    }
  };

  const sortedBatches = useMemo(() => (
    [...batches].sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0))
  ), [batches]);

  const studentById = useMemo(() => {
    const map = new Map();
    students.forEach((s) => map.set(String(s._id), s));
    return map;
  }, [students]);

  const getPaymentStudent = useCallback((payment) => {
    if (payment.student && typeof payment.student === 'object' && payment.student.fullName) {
      return payment.student;
    }
    const id = payment.student?._id || payment.student;
    return studentById.get(String(id)) || null;
  }, [studentById]);

  const loadBalances = useCallback(async (studentList, paymentList) => {
    try {
      const data = await paymentService.getBalances();
      if (Array.isArray(data) && data.length > 0) {
        setBalances(data);
        return;
      }
    } catch {
      // fall through to client-side calculation
    }
    if (studentList.length > 0) {
      setBalances(computeAllBalances(studentList, paymentList));
    }
  }, []);

  useEffect(() => {
    if (students.length === 0) return;
    loadBalances(students, payments);
  }, [students, payments, loadBalances]);

  useEffect(() => {
    const studentId = searchParams.get('student');
    if (!studentId || students.length === 0) return;
    const exists = students.some((s) => String(s._id) === String(studentId));
    if (!exists) return;
    openPaymentForStudent(studentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, students]);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 4000); return () => clearTimeout(t); }
  }, [successMessage]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  const fetchPayments = async () => {
    try { setLoading(true); setError(''); const data = await paymentService.getAll(); setPayments(data); }
    catch (err) { setError(err.error || err.message || 'Failed to fetch payments'); }
    finally { setLoading(false); }
  };

  const fetchStudents = async () => {
    try { const data = await studentService.getAllStudents(); setStudents(data); }
    catch (err) { console.error('Error loading students:', err); }
  };

  const fetchBalances = () => loadBalances(students, payments);

  const resolveStudentSummary = async (studentId) => {
    const student = students.find((s) => String(s._id) === String(studentId));
    if (!student) throw new Error('Student not found');
    try {
      return await paymentService.getStudentSummary(studentId);
    } catch {
      return computePaymentSummary(student, payments);
    }
  };

  const openPaymentForStudent = async (studentId) => {
    try {
      setError('');
      const summary = await resolveStudentSummary(studentId);
      setPaymentSummary(summary);
      setFormData((prev) => ({
        ...prev,
        student: studentId,
        method: 'office',
        officeNote: '',
        amount: summary.balance > 0 ? String(summary.balance) : '',
      }));
      setReceiptFile(null);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.error || err.message || 'Failed to load student balance');
    }
  };

  const openStudentProofs = async (student) => {
    if (!student?._id) return;
    setError('');
    setStudentProofsLoading(true);
    setStudentProofs({ student, summary: null, payments: [] });
    try {
      let summary;
      let studentPayments;
      try {
        const data = await paymentService.getStudentHistory(student._id);
        summary = data.summary;
        studentPayments = Array.isArray(data.payments) ? data.payments : [];
      } catch {
        summary = computePaymentSummary(student, payments);
        studentPayments = payments
          .filter((p) => String(p.student?._id || p.student) === String(student._id))
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      }
      setStudentProofs({ student, summary, payments: studentPayments });
    } catch (err) {
      setError(err.error || err.message || 'Failed to load payment proofs');
      setStudentProofs(null);
    } finally {
      setStudentProofsLoading(false);
    }
  };

  const closeStudentProofs = () => {
    setStudentProofs(null);
    setStudentProofsLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    if (!formData.student) { setError('Please select a student.'); return; }
    const amount = Number(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a valid payment amount.');
      return;
    }
    setSaving(true);
    try {
      const common = { amount, method: formData.method };
      if (formData.method === 'bank_transfer' && receiptFile) {
        await paymentService.createWithReceipt(formData.student, receiptFile, {
          ...common,
          note: formData.officeNote?.trim() || undefined,
        });
      } else {
        const payload = { student: formData.student, ...common };
        if (formData.method === 'bank_transfer' && formData.receiptPath?.trim()) {
          payload.receiptPath = formData.receiptPath.trim();
        } else if (formData.method === 'mobile_money' && formData.paymentProof?.trim()) {
          payload.paymentProof = formData.paymentProof.trim();
        } else if (formData.method === 'office') {
          const today = new Date().toISOString().split('T')[0];
          payload.note = formData.officeNote?.trim() || undefined;
          payload.receiptPath = formData.officeNote?.trim()
            || `Office payment recorded on ${today}`;
        }
        if (!payload.receiptPath && !payload.paymentProof) {
          setError('Please add payment details or use office quick record.');
          setSaving(false);
          return;
        }
        await paymentService.create(payload);
      }
      const remaining = paymentSummary ? paymentSummary.balance - amount : 0;
      setSuccessMessage(
        remaining > 0
          ? `Partial payment of ${formatRwf(amount)} recorded. ${formatRwf(remaining)} still owed.`
          : 'Payment recorded successfully!'
      );
      resetForm();
      fetchPayments();
      fetchBalances();
    } catch (err) { setError(err.error || err.message || 'Failed to add payment'); }
    finally { setSaving(false); }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteConfirm) return;
    setError(''); setSuccessMessage('');
    try {
      await paymentService.delete(deleteConfirm._id);
      setSuccessMessage('Payment removed.'); setDeleteConfirm(null);
      fetchPayments();
      fetchBalances();
    } catch (err) { setError(err.error || err.message || 'Failed to delete payment'); setDeleteConfirm(null); }
  };

  const openEditPayment = (payment) => {
    const methodType = getMethodType(payment);
    setEditPayment(payment);
    setEditFormData({
      paymentProof: payment.paymentProof || '',
      receiptPath: payment.receiptPath || '',
      officeNote: payment.note || (methodType === 'office' ? (payment.receiptPath || '') : ''),
    });
    setActionMenuPayment(null);
    setViewPayment(null);
  };

  const closeEditPayment = () => {
    setEditPayment(null);
    setEditFormData({ paymentProof: '', receiptPath: '', officeNote: '' });
    setEditSaving(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editPayment) return;
    setError('');
    setEditSaving(true);
    try {
      const methodType = getMethodType(editPayment);
      const payload = {};
      if (methodType === 'momo') {
        const proof = editFormData.paymentProof.trim();
        if (!proof) {
          setError('Payment proof is required.');
          setEditSaving(false);
          return;
        }
        payload.paymentProof = proof;
      } else if (methodType === 'bank') {
        const ref = editFormData.receiptPath.trim();
        if (!ref) {
          setError('Bank receipt reference is required.');
          setEditSaving(false);
          return;
        }
        payload.receiptPath = ref;
      } else {
        const note = editFormData.officeNote.trim();
        payload.note = note || undefined;
        payload.receiptPath = note || editFormData.receiptPath.trim() || editPayment.receiptPath;
      }
      await paymentService.update(editPayment._id, payload);
      setSuccessMessage('Payment proof updated.');
      closeEditPayment();
      fetchPayments();
    } catch (err) {
      setError(err.error || err.message || 'Failed to update payment');
    } finally {
      setEditSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ student: '', method: 'office', amount: '', receiptPath: '', paymentProof: '', officeNote: '' });
    setReceiptFile(null);
    setPaymentSummary(null);
    setShowForm(false);
    if (searchParams.get('student')) {
      setSearchParams({}, { replace: true });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStudentName = p => (!p.student ? '—' : typeof p.student === 'object' ? p.student.fullName : '—');
  const getStudentEmail = p => (!p.student || typeof p.student !== 'object' ? '—' : p.student.email || '—');
  const getStudentPhone = p => (!p.student || typeof p.student !== 'object' ? '—' : p.student.phone || p.student.email || '—');
  const getPaymentMethod = (p) => {
    if (p.method === 'office') return 'Office';
    if (p.method === 'bank_transfer' || p.receiptPath) return 'Bank Transfer';
    if (p.method === 'mobile_money' || p.paymentProof) return 'Mobile Money';
    return '—';
  };
  const getMethodType = (p) => {
    if (p.method === 'office') return 'office';
    if (p.method === 'bank_transfer' || p.receiptPath) return 'bank';
    if (p.method === 'mobile_money' || p.paymentProof) return 'momo';
    return 'none';
  };
  const getMethodBadgeClass = (p) => {
    const t = getMethodType(p);
    if (t === 'office') return 'pm-badge-office';
    if (t === 'bank') return 'pm-badge-bank';
    return 'pm-badge-momo';
  };
  const getMethodIcon = (p) => {
    const t = getMethodType(p);
    if (t === 'office') return '🏫';
    if (t === 'bank') return '🏦';
    if (t === 'momo') return '📱';
    return '—';
  };

  const buildReceiptLineItems = (payment) => {
    const student = payment.student && typeof payment.student === 'object' ? payment.student : {};
    const amount = payment.amount || getTrainingFee(student.studentType, student.course).amount || SECONDARY_TERM_FEE;
    const desc = student.fieldOfStudy
      ? `Training Program — ${student.fieldOfStudy}`
      : student.course || 'Training Program';
    return [{ description: desc, quantity: 1, unitPrice: amount }];
  };

  const generateReceiptFor = async (payment) => {
    if (!payment) return;
    setGeneratingReceiptId(payment._id);
    setError('');
    try {
      const lineItems = buildReceiptLineItems(payment);
      await generatePaymentInvoicePdf({
        studentName: getStudentName(payment),
        studentPhone: getStudentPhone(payment),
        studentEmail: getStudentEmail(payment) !== '—' ? getStudentEmail(payment) : undefined,
        lineItems,
        paymentDate: payment.createdAt || new Date().toISOString(),
        paymentMethod: getPaymentMethod(payment),
        amountPaid: payment.amount || lineItems.reduce((s, i) => s + (i.quantity || 0) * (i.unitPrice || 0), 0),
        receiptId: payment._id,
      });
      setSuccessMessage('Receipt PDF downloaded.');
    } catch (err) {
      setError(err.message || 'Failed to generate receipt PDF');
    } finally {
      setGeneratingReceiptId(null);
    }
  };

  const getViewProofSection = (p) => {
    const t = getMethodType(p);
    if (t === 'momo') return { title: 'Mobile Money Proof', type: 'momo' };
    if (t === 'bank') return { title: 'Bank Receipt', type: 'bank' };
    return { title: 'Payment Note', type: 'office' };
  };

  const getViewMethodPillClass = (p) => {
    const t = getMethodType(p);
    if (t === 'office') return 'office';
    if (t === 'bank') return 'bank';
    return 'momo';
  };

  const filteredPayments = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return payments.filter((p) => {
      const nameMatch = getStudentName(p).toLowerCase().includes(term) || getStudentEmail(p).toLowerCase().includes(term);
      if (!nameMatch) return false;
      if (!batchFilter) return true;
      return matchesBatchFilter(getPaymentStudent(p));
    });
  }, [payments, searchTerm, batchFilter, matchesBatchFilter, getPaymentStudent]);

  const historyTotalPages = Math.max(1, Math.ceil(filteredPayments.length / historyPageSize));
  const safeHistoryPage = Math.min(historyPage, historyTotalPages);
  const historyRangeStart = filteredPayments.length === 0 ? 0 : (safeHistoryPage - 1) * historyPageSize + 1;
  const historyRangeEnd = Math.min(safeHistoryPage * historyPageSize, filteredPayments.length);
  const paginatedPayments = useMemo(() => {
    const start = (safeHistoryPage - 1) * historyPageSize;
    return filteredPayments.slice(start, start + historyPageSize);
  }, [filteredPayments, safeHistoryPage, historyPageSize]);

  const getPaymentBatchLabel = useCallback((payment) => {
    const student = getPaymentStudent(payment);
    return student ? getBatchLabel(student) : '—';
  }, [getPaymentStudent]);

  const selectedBatchName = useMemo(() => {
    if (!batchFilter) return '';
    if (batchFilter === '__none__') return 'No batch assigned';
    return sortedBatches.find((b) => b._id === batchFilter)?.name || 'Selected batch';
  }, [batchFilter, sortedBatches]);

  const selectedBatchObj = useMemo(() => {
    if (!batchFilter || batchFilter === '__none__') return null;
    return sortedBatches.find((b) => b._id === batchFilter) || null;
  }, [batchFilter, sortedBatches]);

  const scopedPayments = useMemo(() => {
    if (!batchFilter) return payments;
    return payments.filter((p) => matchesBatchFilter(getPaymentStudent(p)));
  }, [payments, batchFilter, matchesBatchFilter, getPaymentStudent]);

  // Keep the cohort summary independent from the table search and status filters.
  // This ensures the selected batch total includes every received amount,
  // including payments made by students who are still partially paid.
  const batchBalanceRows = useMemo(() => (
    batchFilter
      ? balanceTableRows.filter(({ student }) => matchesBatchFilter(student))
      : balanceTableRows
  ), [balanceTableRows, batchFilter, matchesBatchFilter]);

  const batchSummary = useMemo(
    () => computeBatchPaymentStats(batchBalanceRows, scopedPayments),
    [batchBalanceRows, scopedPayments]
  );

  const paymentsGroupedByBatch = useMemo(() => {
    const groups = new Map();
    filteredPayments.forEach((p) => {
      const student = getPaymentStudent(p);
      const batchId = student ? (getStudentBatchId(student) || '__none__') : '__none__';
      const label = batchId === '__none__' ? 'No batch assigned' : getBatchLabel(student);
      if (!groups.has(batchId)) {
        groups.set(batchId, { batchId, label, payments: [], total: 0 });
      }
      const group = groups.get(batchId);
      group.payments.push(p);
      group.total += Number(p.amount) || 0;
    });
    return [...groups.values()].sort((a, b) => b.total - a.total);
  }, [filteredPayments, getPaymentStudent]);

  const totalOutstandingRwf = useMemo(
    () => balanceTableRows.reduce((sum, { balance }) => sum + (balance.balance > 0 ? balance.balance : 0), 0),
    [balanceTableRows]
  );

  const filteredOutstandingRwf = useMemo(
    () => filteredBalanceRows.reduce((sum, { balance }) => sum + (balance.balance > 0 ? balance.balance : 0), 0),
    [filteredBalanceRows]
  );

  const owingCount = useMemo(
    () => balanceTableRows.filter(({ student, balance }) => (
      balance.status !== 'paid' && matchesBatchFilter(student)
    )).length,
    [balanceTableRows, matchesBatchFilter]
  );

  const getBalanceStatusBadge = (status) => {
    if (status === 'paid') {
      return <span className="pm-balance-badge pm-balance-paid"><span className="pm-balance-badge-dot" />Paid</span>;
    }
    if (status === 'partial') {
      return <span className="pm-balance-badge pm-balance-partial"><span className="pm-balance-badge-dot" />Partial</span>;
    }
    return <span className="pm-balance-badge pm-balance-unpaid"><span className="pm-balance-badge-dot" />Unpaid</span>;
  };

  // Stats (scoped to batch when a cohort filter is active)
  const bankCount = scopedPayments.filter((p) => getMethodType(p) === 'bank').length;
  const momoCount = scopedPayments.filter((p) => getMethodType(p) === 'momo').length;
  const officeCount = scopedPayments.filter((p) => getMethodType(p) === 'office').length;
  const displayPaymentCount = batchFilter ? batchSummary.txCount : payments.length;
  const displayOutstanding = batchFilter ? batchSummary.outstanding : totalOutstandingRwf;
  const displayPartialCount = batchFilter ? batchSummary.partial : balances.filter((b) => b.status === 'partial').length;

  const avatarPalette = ['linear-gradient(135deg,#10B981,#059669)', 'linear-gradient(135deg,#F59E0B,#D97706)', 'linear-gradient(135deg,#8B5CF6,#6366F1)', 'linear-gradient(135deg,#F472B6,#DB2777)', 'linear-gradient(135deg,#60A5FA,#3B82F6)'];
  const getAvatarColor = name => avatarPalette[(name?.charCodeAt(0) || 0) % avatarPalette.length];
  const getInitials = name => name && name !== '—' ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

  const selectedStudentObj = students.find((s) => String(s._id) === String(formData.student));

  const renderPaymentHistoryRow = (p, i = 0) => (
    <tr key={p._id} style={{ animationDelay: `${i * 30}ms` }}>
      <td>
        <div className="pm-student-cell">
          <div className="pm-ava" style={{ background: getAvatarColor(getStudentName(p)) }}>
            {getInitials(getStudentName(p))}
          </div>
          <div>
            <div className="pm-cell-name">{getStudentName(p)}</div>
            <div className="pm-cell-email">{getStudentEmail(p)}</div>
          </div>
        </div>
      </td>
      <td style={{ color: 'var(--muted2)', fontSize: '12px' }}>{getPaymentBatchLabel(p)}</td>
      <td>
        <div className="pm-amount-cell">
          {p.amount ? formatRwf(p.amount) : '—'}
        </div>
      </td>
      <td>
        <span className={`pm-method-badge ${getMethodBadgeClass(p)}`}>
          {getMethodIcon(p)} {getPaymentMethod(p)}
        </span>
      </td>
      <td>
        <div className="pm-date-cell">
          {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
        </div>
      </td>
      <td>
        <div className="pm-td-actions">
          <button
            type="button"
            className="pm-btn-more"
            onClick={() => setActionMenuPayment(p)}
            aria-label={`Actions for ${getStudentName(p)}`}
          >
            ⋮
          </button>
        </div>
      </td>
    </tr>
  );

  const renderPaymentHistoryCard = (p, i = 0) => (
    <div key={p._id} className="pm-card" style={{ animationDelay: `${i * 40}ms` }}>
      <div className="pm-card-head">
        <div className="pm-card-av" style={{ background: getAvatarColor(getStudentName(p)) }}>
          {getInitials(getStudentName(p))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="pm-card-name">{getStudentName(p)}</div>
          <div className="pm-card-email">{getStudentEmail(p)}</div>
        </div>
        <span className={`pm-method-badge ${getMethodBadgeClass(p)}`}>
          {getMethodIcon(p)}
        </span>
      </div>
      <div className="pm-card-body">
        <div className="pm-card-row">
          <span className="pm-card-label">Batch</span>
          <span className="pm-card-val">{getPaymentBatchLabel(p)}</span>
        </div>
        <div className="pm-card-row">
          <span className="pm-card-label">Amount</span>
          <span className="pm-card-val">{p.amount ? formatRwf(p.amount) : '—'}</span>
        </div>
        <div className="pm-card-row">
          <span className="pm-card-label">Method</span>
          <span className="pm-card-val">{getPaymentMethod(p)}</span>
        </div>
        <div className="pm-card-row">
          <span className="pm-card-label">Date</span>
          <span className="pm-card-val">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</span>
        </div>
        {(p.receiptPath || p.paymentProof) && (
          <div>
            <div className="pm-card-label" style={{ marginBottom: '4px' }}>Details</div>
            <div className="pm-card-details">{p.receiptPath || p.paymentProof?.slice(0, 60) + (p.paymentProof?.length > 60 ? '…' : '')}</div>
          </div>
        )}
      </div>
      <div className="pm-card-foot">
        <button type="button" className="pm-btn-view" onClick={() => setActionMenuPayment(p)} style={{ flex: 1 }}>⋮ Actions</button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .pm-root,
        .pm-overlay {
          --accent: var(--green);
          --accent2: var(--green);
          --border-hi: rgba(16,185,129,0.35);
        }

        .pm-root {
          background: var(--bg);
          color: var(--text);
          font-family: system-ui, -apple-system, 'Inter', sans-serif;
          min-height: 100vh;
          padding: 28px 24px;
        }

        /* ── TOAST ── */
        .pm-toast {
          position: fixed; top: 20px; right: 20px; z-index: 1000;
          display: flex; align-items: center; gap: 10px;
          padding: 14px 20px; border-radius: 6px;
          font-size: 13px; font-weight: 600; box-shadow: var(--shadow-md);
          animation: pmSlide 0.3s ease; max-width: 380px; pointer-events: none;
        }
        .pm-toast-s { background: rgba(16,185,129,0.12); border: 1px solid rgba(16,185,129,0.3); color: #6EE7B7; }
        .pm-toast-e { background: #fef2f2; border: 1px solid #fecaca; color: var(--error-red); border-left: 4px solid var(--error-red); }
        @keyframes pmSlide { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }

        /* ── PAGE HEADER ── */
        .pm-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 20px; margin-bottom: 28px; flex-wrap: wrap;
        }
        .pm-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2);
          border-radius: 12px; padding: 4px 12px;
          font-size: 11px; font-weight: 700; color: var(--accent);
          letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px;
        }
        .pm-page-title {
          font-family: inherit;
          font-size: 28px; font-weight: 800; letter-spacing: -0.5px; line-height: 1.1;
        }
        .pm-page-title span {
          background: linear-gradient(90deg, var(--accent), var(--accent-orange));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .pm-page-sub { font-size: 13px; color: var(--text-light); margin-top: 6px; }

        /* ── STATS ── */
        .pm-stats {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px; margin-bottom: 20px;
        }
        .pm-stat {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 14px; padding: 16px 18px;
          display: flex; align-items: flex-start; gap: 12px;
          position: relative; overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
          min-width: 0; box-shadow: var(--shadow-sm);
        }
        .pm-stat:hover { transform: translateY(-1px); box-shadow: var(--shadow-md); border-color: #d1fae5; }
        .pm-stat-icon {
          width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; background: var(--bg-i); color: var(--c);
        }
        .pm-stat-body { flex: 1; min-width: 0; }
        .pm-stat-num {
          font-size: clamp(16px, 1.8vw, 22px); font-weight: 800; line-height: 1.15;
          color: var(--text); margin-bottom: 4px;
          font-variant-numeric: tabular-nums; letter-spacing: -0.02em;
        }
        .pm-stat-lbl {
          font-size: 11px; color: var(--text-light); font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em; line-height: 1.3;
        }

        /* ── COHORT BAR & PANEL ── */
        .pm-cohort-bar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; flex-wrap: wrap;
          margin-bottom: 18px;
          padding: 14px 16px;
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 16px;
          box-shadow: var(--shadow-sm);
        }
        .pm-cohort-bar-left {
          display: flex; align-items: center; gap: 12px; flex: 1; min-width: 240px;
        }
        .pm-cohort-bar-icon {
          width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          color: #047857; font-size: 18px;
        }
        .pm-cohort-bar-label {
          font-size: 11px; font-weight: 700; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px;
        }
        .pm-cohort-select {
          min-width: 260px; max-width: 100%;
          padding: 10px 14px; border-radius: 10px;
          border: 1px solid var(--border-light);
          background: var(--surface2); font-size: 14px; font-weight: 600;
          font-family: inherit; color: var(--text-dark);
          cursor: pointer;
        }
        .pm-cohort-select:focus { outline: none; border-color: #6ee7b7; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); }
        .pm-cohort-bar-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .pm-cohort-panel {
          margin-bottom: 20px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fffe 55%, #f0fdf9 100%);
          border: 1px solid #a7f3d0;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(5, 150, 105, 0.08);
        }
        .pm-cohort-panel-head {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 20px; flex-wrap: wrap;
          padding: 22px 24px 18px;
          border-bottom: 1px solid rgba(167, 243, 208, 0.5);
        }
        .pm-cohort-panel-title {
          font-size: 20px; font-weight: 800; color: var(--text);
          letter-spacing: -0.02em; line-height: 1.25; margin-bottom: 8px;
        }
        .pm-cohort-panel-meta {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        }
        .pm-cohort-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 999px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
        }
        .pm-cohort-badge.track { background: var(--link-hover-bg); color: #1d4ed8; border: 1px solid #bfdbfe; }
        .pm-cohort-badge.running { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
        .pm-cohort-badge.dates { background: var(--bg); color: var(--text-light); border: 1px solid var(--border); }
        .pm-cohort-progress-wrap { min-width: 160px; text-align: right; }
        .pm-cohort-progress-pct {
          font-size: 32px; font-weight: 800; color: #047857;
          line-height: 1; letter-spacing: -0.03em;
        }
        .pm-cohort-progress-lbl {
          font-size: 11px; font-weight: 600; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 0.05em; margin-top: 4px;
        }
        .pm-cohort-progress-bar {
          height: 8px; border-radius: 999px; background: #e5e7eb;
          margin-top: 12px; overflow: hidden;
        }
        .pm-cohort-progress-fill {
          height: 100%; border-radius: 999px;
          background: linear-gradient(90deg, #10b981, #059669);
          transition: width 0.4s ease;
        }
        .pm-cohort-panel-body {
          display: grid;
          grid-template-columns: 1.1fr 1fr 1fr;
          gap: 0;
        }
        .pm-cohort-section {
          padding: 20px 24px;
          border-right: 1px solid rgba(167, 243, 208, 0.4);
        }
        .pm-cohort-section:last-child { border-right: none; }
        .pm-cohort-section-title {
          font-size: 11px; font-weight: 700; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 14px;
        }
        .pm-cohort-status-list { display: flex; flex-direction: column; gap: 10px; }
        .pm-cohort-status-row {
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
        }
        .pm-cohort-status-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-weight: 600; color: var(--text-medium);
        }
        .pm-cohort-status-dot {
          width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        }
        .pm-cohort-status-dot.paid { background: #10b981; }
        .pm-cohort-status-dot.partial { background: #f59e0b; }
        .pm-cohort-status-dot.unpaid { background: #ef4444; }
        .pm-cohort-status-val {
          font-size: 15px; font-weight: 800; color: var(--text);
          font-variant-numeric: tabular-nums;
        }
        .pm-cohort-money-val {
          font-size: 22px; font-weight: 800; color: var(--text);
          letter-spacing: -0.02em; font-variant-numeric: tabular-nums;
          margin-bottom: 4px;
        }
        .pm-cohort-money-val.accent { color: #047857; }
        .pm-cohort-money-val.warn { color: #b45309; }
        .pm-cohort-money-sub {
          font-size: 12px; color: var(--text-light); line-height: 1.4;
        }
        .pm-cohort-panel-foot {
          padding: 12px 24px;
          background: rgba(236, 253, 245, 0.5);
          border-top: 1px solid rgba(167, 243, 208, 0.4);
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
          font-size: 12px; color: var(--text-light);
        }
        .pm-cohort-panel-foot strong { color: var(--text-medium); font-weight: 700; }

        .pm-batch-hint {
          display: flex; align-items: center; gap: 14px;
          margin-bottom: 20px; padding: 16px 18px;
          background: var(--background-white);
          border: 1px dashed #d1d5db; border-radius: 16px;
        }
        .pm-batch-hint-icon {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: var(--bg); font-size: 20px;
        }
        .pm-batch-hint-title { font-size: 14px; font-weight: 700; color: var(--text); margin-bottom: 2px; }
        .pm-batch-hint-text { font-size: 13px; color: var(--text-light); line-height: 1.45; }

        .pm-toolbar-card {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 16px;
          padding: 14px 16px;
          margin-bottom: 16px;
          box-shadow: var(--shadow-sm);
        }
        .pm-toolbar-card .pm-toolbar { margin-bottom: 0; }

        /* ── TOOLBAR ── */
        .pm-toolbar {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 24px; flex-wrap: wrap;
        }
        .pm-search {
          flex: 1; min-width: 200px; max-width: 440px;
          display: flex; align-items: center; gap: 10px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 11px 16px;
          transition: border-color 0.2s;
        }
        .pm-search:focus-within { border-color: var(--border-hi); }
        .pm-search-icon { font-size: 15px; color: var(--text-light); flex-shrink: 0; }
        .pm-search input {
          flex: 1; border: none; outline: none; background: none;
          font-size: 13px; color: var(--text-dark); font-family: inherit;
        }
        .pm-search input::placeholder { color: var(--text-light); }
        .pm-clear { background: none; border: none; color: var(--text-light); cursor: pointer; font-size: 15px; padding: 0 2px; transition: color 0.2s; }
        .pm-clear:hover { color: var(--text-dark); }

        .pm-btn-add {
          display: flex; align-items: center; gap: 8px; padding: 11px 20px;
          background: linear-gradient(135deg, var(--accent), var(--accent-orange));
          color: white; border: none; border-radius: 6px;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .pm-btn-add:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .pm-btn-add.cancel { background: rgba(248,113,113,0.1); color: var(--error-red); border: 1px solid rgba(248,113,113,0.3); box-shadow: none; }
        .pm-btn-add.cancel:hover { background: rgba(248,113,113,0.15); transform: none; }

        .pm-btn-refresh {
          display: flex; align-items: center; gap: 6px; padding: 11px 16px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 13px; font-weight: 500; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .pm-btn-refresh:hover { border-color: var(--border-light); color: var(--text-dark); }

        /* ── TABLE CARD ── */
        .pm-section-head {
          display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
        }
        .pm-section-title {
          font-family: inherit; font-size: 17px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .pm-section-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 8px var(--accent); }
        .pm-count-badge {
          background: rgba(16,185,129,0.12); border: 1px solid rgba(16,185,129,0.2);
          color: var(--accent); border-radius: 12px; padding: 2px 10px; font-size: 12px; font-weight: 700;
        }
        .pm-search-hint { font-size: 12px; color: var(--text-light); }
        .pm-search-hint span { color: var(--accent); }

        .pm-table-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; overflow: hidden;
        }
        .pm-scroll { overflow-x: auto; }
        .pm-scroll::-webkit-scrollbar { height: 4px; }
        .pm-scroll::-webkit-scrollbar-thumb { background: var(--border-light); border-radius: 6px; }

        .pm-table { width: 100%; border-collapse: collapse; min-width: 640px; }
        .pm-table th {
          padding: 13px 20px; font-size: 10px; font-weight: 800; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 1px;
          border-bottom: 1px solid var(--border); text-align: left;
          background: var(--border-light); white-space: nowrap;
        }
        .pm-table td {
          padding: 14px 20px; font-size: 13px; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light); vertical-align: middle;
        }
        .pm-table tbody tr { transition: background 0.15s; }
        .pm-table tbody tr:hover { background: rgba(16,185,129,0.03); }
        .pm-table tbody tr:last-child td { border-bottom: none; }

        .pm-student-cell { display: flex; align-items: center; gap: 10px; }
        .pm-ava {
          width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 800; color: white; font-family: inherit;
        }
        .pm-cell-name { font-weight: 600; font-size: 13px; }
        .pm-cell-email { font-size: 11px; color: var(--text-light); margin-top: 1px; }

        .pm-method-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border-radius: 12px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.3px; white-space: nowrap;
        }
        .pm-badge-bank { background: rgba(96,165,250,0.12); color: #1d4ed8; border: 1px solid rgba(96,165,250,0.25); }
        .pm-badge-office { background: rgba(16,185,129,0.12); color: #047857; border: 1px solid rgba(16,185,129,0.25); }
        .pm-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 9px; border-radius: 999px;
          font-size: 11px; font-weight: 700; white-space: nowrap;
        }
        .pm-badge-momo { background: rgba(245,158,11,0.12); color: #b45309; border: 1px solid rgba(245,158,11,0.25); }

        .pm-details-cell {
          max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-size: 12px; color: var(--muted2); font-family: monospace;
        }
        .pm-date-cell { font-size: 12px; color: var(--muted2); white-space: nowrap; }

        .pm-td-actions { display: flex; gap: 6px; align-items: center; justify-content: flex-end; }
        .pm-btn-more {
          width: 36px; height: 36px; border-radius: 8px;
          border: 1px solid var(--border-medium); background: var(--surface);
          color: var(--text-medium); font-size: 18px; line-height: 1;
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .pm-btn-more:hover { background: var(--surface2); border-color: var(--border-hi); color: var(--text-dark); }
        .pm-actions-menu-modal {
          width: min(380px, calc(100vw - 32px));
          background: var(--surface); border-radius: 18px;
          border: 1px solid var(--border);
          box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.04);
          overflow: hidden; animation: pmModalIn 0.22s cubic-bezier(0.34, 1.2, 0.64, 1);
        }
        .pm-actions-menu-head {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
          padding: 20px 20px 16px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          border-bottom: 1px solid #f1f5f9;
        }
        .pm-actions-menu-head-main { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
        .pm-actions-menu-av {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 800; color: #fff;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
        }
        .pm-actions-menu-head-text { min-width: 0; }
        .pm-actions-menu-eyebrow {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #64748b; margin-bottom: 4px;
        }
        .pm-actions-menu-title {
          font-size: 16px; font-weight: 700; color: var(--text);
          line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .pm-actions-menu-meta {
          display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 8px;
        }
        .pm-actions-menu-amount {
          font-size: 12px; font-weight: 700; color: #047857;
          background: #ecfdf5; border: 1px solid #a7f3d0;
          padding: 3px 8px; border-radius: 999px;
        }
        .pm-actions-menu-method {
          font-size: 11px; font-weight: 600; color: #475569;
          background: var(--bg); border: 1px solid #e2e8f0;
          padding: 3px 8px; border-radius: 999px;
        }
        .pm-actions-menu-date { font-size: 11px; color: var(--muted); }
        .pm-actions-menu-close {
          width: 34px; height: 34px; border: 1px solid #e2e8f0; border-radius: 10px;
          background: var(--surface); color: #64748b; cursor: pointer; font-size: 15px;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: all 0.15s;
        }
        .pm-actions-menu-close:hover { background: var(--bg); color: var(--text); border-color: #cbd5e1; }
        .pm-actions-menu-body { padding: 14px 16px 16px; }
        .pm-actions-menu-label {
          font-size: 10px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--muted); margin-bottom: 10px; padding-left: 2px;
        }
        .pm-actions-menu-list { display: flex; flex-direction: column; gap: 6px; }
        .pm-actions-menu-item {
          display: flex; align-items: center; gap: 12px; width: 100%;
          padding: 12px 12px; border: 1px solid #f1f5f9; border-radius: 12px;
          background: var(--surface); color: var(--text);
          font-family: inherit; cursor: pointer; text-align: left;
          transition: border-color 0.15s, background 0.15s, box-shadow 0.15s, transform 0.15s;
        }
        .pm-actions-menu-item:hover {
          background: var(--bg); border-color: #e2e8f0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05); transform: translateY(-1px);
        }
        .pm-actions-menu-item:active { transform: translateY(0); }
        .pm-actions-menu-item-text { flex: 1; min-width: 0; }
        .pm-actions-menu-item-title { display: block; font-size: 14px; font-weight: 600; color: var(--text); line-height: 1.3; }
        .pm-actions-menu-item-desc { display: block; font-size: 12px; color: #64748b; margin-top: 2px; line-height: 1.35; }
        .pm-actions-menu-item-chevron { color: #cbd5e1; font-size: 18px; font-weight: 300; flex-shrink: 0; }
        .pm-actions-menu-item:hover .pm-actions-menu-item-chevron { color: var(--muted); }
        .pm-actions-menu-item-icon {
          width: 40px; height: 40px; border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .pm-actions-menu-item-icon svg { width: 18px; height: 18px; }
        .pm-actions-menu-item-icon.view { background: var(--link-hover-bg); color: #2563eb; border: 1px solid #dbeafe; }
        .pm-actions-menu-item-icon.invoice { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
        .pm-actions-menu-item-icon.edit { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
        .pm-actions-menu-item-icon.delete { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .pm-actions-menu-divider {
          height: 1px; background: var(--bg); margin: 12px 0 10px;
        }
        .pm-actions-menu-item.danger {
          border-color: #fee2e2; background: #fffbfb;
        }
        .pm-actions-menu-item.danger .pm-actions-menu-item-title { color: #b91c1c; }
        .pm-actions-menu-item.danger:hover {
          background: #fef2f2; border-color: #fecaca;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.08);
        }
        .pm-invoice-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .pm-invoice-table th, .pm-invoice-table td { padding: 8px; border-bottom: 1px solid var(--border-light); font-size: 13px; }
        .pm-invoice-table th { text-align: left; color: var(--text-light); font-size: 11px; text-transform: uppercase; }
        .pm-invoice-table input {
          width: 100%; padding: 8px 10px; border: 1px solid var(--border-medium);
          border-radius: 8px; font-size: 13px; font-family: inherit; color: var(--text-dark);
          background: var(--surface);
        }
        .pm-invoice-table input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
        .pm-btn-add-line, .pm-btn-remove-line {
          border: 1px solid var(--border-medium); background: var(--surface2);
          border-radius: 8px; padding: 6px 10px; font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .pm-btn-add-line { margin-top: 10px; color: var(--accent2); }
        .pm-btn-remove-line { color: #b91c1c; }
        .pm-btn-view, .pm-btn-del {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 7px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s; border: 1px solid; white-space: nowrap;
        }
        .pm-btn-view { background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.2); color: var(--blue); }
        .pm-btn-view:hover { background: rgba(96,165,250,0.15); border-color: rgba(96,165,250,0.4); }
        .pm-btn-del { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.2); color: var(--error-red); }
        .pm-btn-del:hover { background: rgba(248,113,113,0.15); border-color: rgba(248,113,113,0.4); }

        /* ── MOBILE CARDS ── */
        .pm-cards { display: none; flex-direction: column; gap: 12px; padding: 16px; }
        .pm-card {
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; overflow: hidden;
          animation: pmFadeUp 0.3s ease both; transition: border-color 0.2s;
        }
        .pm-card:hover { border-color: var(--border-light); }
        @keyframes pmFadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .pm-card-head { display: flex; align-items: center; gap: 12px; padding: 16px; border-bottom: 1px solid var(--border); }
        .pm-card-av {
          width: 44px; height: 44px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 15px; font-weight: 800; color: white; font-family: inherit;
        }
        .pm-card-name { font-family: inherit; font-size: 14px; font-weight: 700; margin-bottom: 2px; }
        .pm-card-email { font-size: 11px; color: var(--text-light); }
        .pm-card-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
        .pm-card-fees-strip {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 12px 14px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 10px;
        }
        .pm-card-fee {
          display: flex;
          flex-direction: column;
          gap: 4px;
          text-align: center;
        }
        .pm-card-fee-lbl {
          font-size: 10px;
          font-weight: 700;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .pm-card-fee-val {
          font-size: 13px;
          font-weight: 700;
          color: var(--text-dark);
          font-variant-numeric: tabular-nums;
        }
        .pm-card-fee.highlight .pm-card-fee-val {
          font-size: 15px;
          color: #d97706;
        }
        .pm-card-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .pm-card-label { font-size: 10px; font-weight: 700; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; }
        .pm-card-val { font-size: 12px; color: var(--text-dark); text-align: right; }
        .pm-card-details { font-size: 11px; color: var(--muted2); font-family: monospace; word-break: break-all; }
        .pm-card-foot { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--border); }
        .pm-card-foot .pm-btn-view, .pm-card-foot .pm-btn-del { flex: 1; justify-content: center; }

        /* ── MODAL BASE ── */
        .pm-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: pmFadeIn 0.2s ease;
        }
        @keyframes pmFadeIn { from { opacity:0; } to { opacity:1; } }
        .pm-modal {
          background: var(--surface); border: 1px solid var(--border-light);
          border-radius: 16px; width: 100%; max-height: 90vh;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: var(--shadow-lg);
          animation: pmModalIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes pmModalIn { from { opacity:0; transform:scale(0.93); } to { opacity:1; transform:scale(1); } }
        .pm-modal-sm { max-width: 520px; }
        .pm-modal-lg { max-width: 620px; }
        .pm-modal-add {
          border: 1px solid var(--border-medium);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
        }

        .pm-modal-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 22px 24px; border-bottom: 1px solid var(--border-light);
          flex-shrink: 0; background: var(--surface);
        }
        .pm-modal-head-left { display: flex; align-items: center; gap: 14px; }
        .pm-modal-icon {
          width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
        }
        .pm-modal-icon-green {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #047857;
        }
        .pm-modal-icon-blue  { background: linear-gradient(135deg, rgba(96,165,250,0.2), rgba(59,130,246,0.15)); border: 1px solid rgba(96,165,250,0.3); }
        .pm-modal-icon-red   { background: linear-gradient(135deg, rgba(248,113,113,0.2), rgba(239,68,68,0.15)); border: 1px solid rgba(248,113,113,0.3); }
        .pm-modal-title { font-family: inherit; font-size: 18px; font-weight: 700; color: var(--text-dark); }
        .pm-modal-sub { font-size: 13px; color: var(--text-light); margin-top: 3px; }
        .pm-modal-close {
          width: 36px; height: 36px; border-radius: 8px;
          background: var(--surface); border: 1px solid var(--border-medium);
          color: var(--text-medium); font-size: 16px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s; flex-shrink: 0;
        }
        .pm-modal-close:hover { color: var(--text-dark); background: var(--surface3); border-color: #9ca3af; }

        .pm-modal-body {
          padding: 24px; overflow-y: auto; flex: 1;
          display: flex; flex-direction: column; gap: 20px;
          background: var(--surface);
        }
        .pm-modal-body::-webkit-scrollbar { width: 5px; }
        .pm-modal-body::-webkit-scrollbar-thumb { background: var(--border-medium); border-radius: 6px; }

        .pm-modal-foot {
          padding: 16px 24px; border-top: 1px solid var(--border-light);
          display: flex; gap: 12px; justify-content: flex-end; flex-shrink: 0;
          background: var(--bg);
        }

        /* ── FORM FIELDS ── */
        .pm-field { display: flex; flex-direction: column; gap: 8px; }
        .pm-label {
          font-size: 12px; font-weight: 700; color: var(--text-medium);
          text-transform: uppercase; letter-spacing: 0.05em;
          display: flex; align-items: center; gap: 4px;
        }
        .pm-req { color: var(--error-red); font-size: 13px; line-height: 1; }
        .pm-field-hint {
          font-size: 12px; color: var(--text-light); line-height: 1.45; margin-top: -2px;
        }
        .pm-input, .pm-select, .pm-textarea {
          background-color: #ffffff;
          border: 1.5px solid var(--border-medium);
          border-radius: 8px; padding: 12px 14px;
          font-size: 14px; font-weight: 500; color: var(--text-dark); font-family: inherit;
          outline: none; width: 100%;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
        }
        .pm-input:hover, .pm-select:hover, .pm-textarea:hover {
          border-color: #9ca3af;
        }
        .pm-input:focus, .pm-select:focus, .pm-textarea:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
        }
        .pm-input::placeholder, .pm-textarea::placeholder { color: #9ca3af; font-weight: 400; }
        .pm-select {
          appearance: none; cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23374151' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 14px;
          padding-right: 40px;
        }
        .pm-select option { background: var(--surface); color: var(--text); }
        .pm-textarea { resize: vertical; min-height: 100px; padding-right: 14px; }

        /* Method tab picker */
        .pm-method-picker { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        .pm-method-opt {
          padding: 16px 12px; border-radius: 10px;
          border: 1.5px solid var(--border-medium);
          background: var(--surface);
          cursor: pointer; text-align: center; transition: all 0.2s;
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          font-family: inherit;
        }
        .pm-method-opt:hover {
          border-color: #9ca3af;
          background: var(--bg);
        }
        .pm-method-opt.active-bank {
          border-color: #3b82f6;
          background: var(--link-hover-bg);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
        }
        .pm-method-opt.active-momo {
          border-color: #f59e0b;
          background: #fffbeb;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12);
        }
        .pm-method-opt.active-office {
          border-color: #059669;
          background: #ecfdf5;
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.12);
        }
        .pm-method-opt-icon { font-size: 24px; line-height: 1; }
        .pm-method-opt-label {
          font-size: 12px; font-weight: 700; color: var(--text-medium);
        }
        .pm-method-opt.active-bank .pm-method-opt-label { color: #1d4ed8; }
        .pm-method-opt.active-momo .pm-method-opt-label { color: #b45309; }
        .pm-method-opt.active-office .pm-method-opt-label { color: #047857; }

        .pm-file-upload {
          border: 1.5px dashed var(--border-medium);
          border-radius: 10px;
          padding: 18px 16px;
          text-align: center;
          background: #fafafa;
          cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
        }
        .pm-file-upload:hover { border-color: var(--accent); background: #f0fdf4; }
        .pm-file-upload.has-file { border-color: #059669; background: #ecfdf5; border-style: solid; }
        .pm-file-upload input { display: none; }
        .pm-file-upload-label { font-size: 13px; color: var(--text-medium); font-weight: 600; }
        .pm-file-upload-name { font-size: 12px; color: var(--text-light); margin-top: 6px; }

        .pm-unpaid-panel {
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 20px 22px;
          margin-bottom: 24px;
          box-shadow: var(--shadow-sm);
        }
        .pm-unpaid-head {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; flex-wrap: wrap; margin-bottom: 14px;
        }
        .pm-unpaid-title { font-size: 16px; font-weight: 700; color: #92400e; }
        .pm-unpaid-sub { font-size: 13px; color: #a16207; margin-top: 4px; line-height: 1.45; max-width: 520px; }
        .pm-unpaid-add-btn {
          padding: 10px 16px; border-radius: 8px; border: none;
          background: #d97706; color: #fff; font-size: 13px; font-weight: 700;
          cursor: pointer; font-family: inherit;
        }
        .pm-unpaid-add-btn:hover { background: #b45309; }
        .pm-unpaid-search input {
          width: 100%; padding: 10px 14px; border: 1px solid #fde68a;
          border-radius: 8px; font-size: 13px; margin-bottom: 12px;
          background: var(--surface);
        }
        .pm-unpaid-list { display: flex; flex-direction: column; gap: 8px; }
        .pm-unpaid-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap;
          padding: 12px 14px; background: var(--surface);
          border: 1px solid #fde68a; border-radius: 10px;
        }
        .pm-unpaid-student { display: flex; align-items: center; gap: 10px; }
        .pm-unpaid-record-btn {
          padding: 8px 14px; border-radius: 8px; border: none;
          background: #059669; color: #fff; font-size: 12px; font-weight: 700;
          cursor: pointer; font-family: inherit; white-space: nowrap;
        }
        .pm-unpaid-record-btn:hover { background: #047857; }
        .pm-unpaid-more { font-size: 12px; color: #a16207; margin-top: 10px; text-align: center; }
        .pm-unpaid-balance { font-size: 12px; color: #92400e; margin-top: 4px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pm-balance-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.3px; }
        .pm-balance-partial { background: #fef3c7; color: #92400e; }
        .pm-balance-unpaid { background: #fee2e2; color: #991b1b; }
        .pm-balance-paid { background: #ecfdf5; color: #047857; }
        .pm-balance-due { font-weight: 700; color: #b45309; }
        .pm-balance-zero { color: #047857; font-weight: 700; }

        /* ── Student balances list ── */
        .pm-balances-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }
        .pm-balances-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          padding: 18px 20px;
          border-bottom: 1px solid var(--border);
          background: var(--surface);
        }
        .pm-balances-card-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
          letter-spacing: -0.01em;
        }
        .pm-balances-card-meta {
          font-size: 13px;
          color: var(--text2);
          margin-top: 4px;
        }
        .pm-balances-card-meta strong {
          color: var(--text);
          font-weight: 700;
        }
        .pm-balances-outstanding-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 10px;
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          color: #b45309;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }
        .pm-balances-outstanding-pill span {
          font-weight: 500;
          color: var(--text2);
          font-size: 12px;
        }

        .pm-balance-list {
          display: flex;
          flex-direction: column;
        }
        .pm-balance-list-head {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(132px, auto);
          align-items: center;
          gap: 16px 28px;
          padding: 10px 20px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: var(--muted);
          background: var(--surface2);
          border-bottom: 1px solid var(--border);
        }
        .pm-balance-list-head-fees {
          display: flex;
          align-items: center;
          gap: 28px;
          justify-content: flex-end;
        }
        .pm-balance-list-head-fees span {
          min-width: 76px;
          text-align: right;
        }
        .pm-balance-list-head-fees span:last-child {
          min-width: 88px;
        }

        .pm-balance-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(132px, auto);
          align-items: center;
          gap: 16px 28px;
          padding: 14px 20px;
          border-bottom: 1px solid var(--border);
          border-left: 3px solid transparent;
          transition: background 0.15s ease;
          animation: pmFadeUp 0.3s ease both;
        }
        .pm-balance-row:last-child { border-bottom: none; }
        .pm-balance-row:hover { background: var(--row-hover); }
        .pm-balance-row.status-unpaid { border-left-color: #ef4444; }
        .pm-balance-row.status-partial { border-left-color: #f59e0b; }
        .pm-balance-row.status-paid { border-left-color: #10b981; }

        .pm-balance-row-student {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .pm-balance-ava {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 800;
          color: #fff;
        }
        .pm-balance-student-body {
          min-width: 0;
          flex: 1;
        }
        .pm-balance-student-top {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          min-width: 0;
        }
        .pm-balance-student-name {
          font-size: 14px;
          font-weight: 700;
          color: var(--text);
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pm-balance-student-program {
          margin-top: 3px;
          font-size: 12px;
          color: var(--text2);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .pm-balance-batch-chip {
          display: inline-flex;
          margin-top: 5px;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
          color: var(--accent);
          background: var(--accent-glow);
          border: 1px solid rgba(59, 130, 246, 0.15);
        }

        .pm-balance-row-fees {
          display: flex;
          align-items: center;
          gap: 28px;
          flex-shrink: 0;
        }
        .pm-balance-fee-item {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          min-width: 76px;
        }
        .pm-balance-fee-item.outstanding {
          min-width: 88px;
        }
        .pm-balance-fee-lbl {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--muted);
        }
        .pm-balance-fee-val {
          font-size: 13px;
          font-weight: 700;
          color: var(--text);
          font-variant-numeric: tabular-nums;
        }
        .pm-balance-fee-val.muted { color: var(--text2); font-weight: 600; }
        .pm-balance-fee-val.due {
          font-size: 15px;
          font-weight: 800;
          color: #d97706;
        }
        .pm-balance-fee-val.clear {
          font-size: 15px;
          font-weight: 800;
          color: var(--green);
        }

        .pm-balance-row-action {
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .pm-balance-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 10px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .pm-balance-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
        }
        .pm-balance-partial {
          background: rgba(245, 158, 11, 0.12);
          color: #b45309;
        }
        .pm-balance-unpaid {
          background: rgba(239, 68, 68, 0.1);
          color: #b91c1c;
        }
        .pm-balance-paid {
          background: rgba(16, 185, 129, 0.12);
          color: #047857;
        }

        .pm-btn-record-balance {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 14px;
          border-radius: 10px;
          border: none;
          background: #059669;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          white-space: nowrap;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
          transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
        }
        .pm-btn-record-balance svg {
          width: 15px;
          height: 15px;
          flex-shrink: 0;
        }
        .pm-btn-record-balance:hover {
          background: #047857;
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
          transform: translateY(-1px);
        }
        .pm-balance-settled {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          font-weight: 600;
          color: var(--green);
        }
        .pm-balance-settled svg {
          width: 16px;
          height: 16px;
        }

        /* legacy balance table helpers (payment history still uses pm-table) */
        .pm-balance-student { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .pm-balance-student-meta { display: none; }
        .pm-balance-program { display: none; }
        .pm-balance-fees { display: none; }
        .pm-balance-amount { font-weight: 600; color: var(--text); }
        .pm-balance-amount.muted { color: var(--text2); font-weight: 500; }
        .pm-balance-amount.due { font-weight: 800; font-size: 14px; color: #d97706; }
        .pm-balance-amount.clear { font-weight: 700; color: var(--green); }
        .pm-balance-progress,
        .pm-balance-progress-top,
        .pm-balance-progress-track,
        .pm-balance-progress-fill { display: none; }
        .pm-balance-no-action { font-size: 12px; color: var(--muted); font-weight: 500; }

        .pm-view-tabs {
          display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px;
        }
        .pm-view-tab {
          padding: 10px 16px; border-radius: 10px; border: 1px solid var(--border-light);
          background: var(--surface); color: var(--text-medium); font-size: 13px; font-weight: 700;
          cursor: pointer; font-family: inherit; transition: all 0.15s;
        }
        .pm-view-tab:hover { border-color: #a7f3d0; color: #047857; }
        .pm-view-tab.active {
          background: linear-gradient(135deg, #ecfdf5, #ffffff);
          border-color: #6ee7b7; color: #047857;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        .pm-toolbar-filters {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .pm-balance-filter {
          padding: 10px 12px; border-radius: 8px; border: 1px solid var(--border-light);
          background: var(--surface); font-size: 13px; font-family: inherit; color: var(--text-dark);
          min-width: 170px;
        }
        .pm-outstanding-total {
          font-size: 12px; color: #92400e; font-weight: 600;
          padding: 8px 12px; border-radius: 8px; background: #fffbeb; border: 1px solid #fde68a;
        }
        .pm-group-toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--text-medium);
          cursor: pointer;
          white-space: nowrap;
          user-select: none;
        }
        .pm-group-toggle input { accent-color: var(--accent); }
        .pm-batch-group { margin-bottom: 20px; }
        .pm-batch-group-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 14px;
          background: var(--surface2);
          border: 1px solid var(--border);
          border-bottom: none;
          border-radius: 14px 14px 0 0;
          font-size: 13px;
          font-weight: 700;
          color: var(--text);
        }
        .pm-batch-group-meta { font-size: 12px; font-weight: 600; color: var(--text-light); }
        .pm-batch-group .pm-table-card { border-radius: 0 0 14px 14px; margin: 0; border-top: none; }
        .pm-student-batch-chip {
          display: inline-flex;
          margin-top: 8px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          color: #1d4ed8;
          background: var(--link-hover-bg);
          border: 1px solid #bfdbfe;
          border-radius: 999px;
        }
        .pm-btn-record-sm {
          padding: 8px 12px; border-radius: 8px; border: none;
          background: #059669; color: #fff; font-size: 12px; font-weight: 700;
          cursor: pointer; font-family: inherit; white-space: nowrap;
        }
        .pm-btn-record-sm:hover { background: #047857; }

        .pm-pagination {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          flex-wrap: wrap; padding: 14px 20px;
          border-top: 1px solid var(--border-light); background: var(--bg);
        }
        .pm-pagination-info { font-size: 13px; color: var(--text-light); }
        .pm-pagination-info strong { color: var(--text-dark); font-weight: 700; }
        .pm-pagination-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pm-pagination-size {
          padding: 8px 12px; border-radius: 8px; border: 1px solid var(--border-light);
          background: var(--surface); font-size: 13px; font-family: inherit; color: var(--text-dark); cursor: pointer;
        }
        .pm-pagination-btn {
          padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border-light);
          background: var(--surface); font-size: 13px; font-weight: 600; font-family: inherit;
          color: var(--text-dark); cursor: pointer; transition: all 0.15s;
        }
        .pm-pagination-btn:hover:not(:disabled) {
          border-color: var(--accent); color: var(--accent); background: #ecfdf5;
        }
        .pm-pagination-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .pm-pagination-page {
          font-size: 13px; font-weight: 600; color: var(--text-dark);
          padding: 0 8px; min-width: 100px; text-align: center;
        }
        .pm-badge-office { background: rgba(5,150,105,0.12); color: #047857; }
        .pm-fee-summary {
          background: var(--surface2); border: 1px solid var(--border); border-radius: 12px;
          padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; margin-bottom: 4px;
        }
        .pm-fee-row { display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: var(--text-medium); }
        .pm-fee-row strong { color: var(--text); font-weight: 700; }
        .pm-fee-balance { padding-top: 8px; border-top: 1px dashed var(--border); color: var(--accent); }
        .pm-fee-balance strong { color: var(--accent2); font-size: 15px; }
        .pm-amount-quick { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
        .pm-amount-quick button {
          font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 8px;
          border: 1px solid var(--border-hi); background: rgba(16,185,129,0.08); color: var(--accent2);
          cursor: pointer; transition: background 0.15s;
        }
        .pm-amount-quick button:hover { background: rgba(16,185,129,0.15); }
        .pm-amount-cell { font-weight: 700; color: var(--accent2); white-space: nowrap; }

        /* Student preview chip */
        .pm-student-preview {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px; background: #ecfdf5;
          border: 1px solid #a7f3d0; border-radius: 10px;
        }
        .pm-student-locked { margin-top: 0; }
        .pm-preview-av {
          width: 30px; height: 30px; border-radius: 6px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white; font-family: inherit;
        }
        .pm-preview-name { font-size: 13px; font-weight: 600; }
        .pm-preview-email { font-size: 11px; color: var(--text-light); }

        /* ── VIEW MODAL DETAILS ── */
        .pm-modal-view { max-width: 560px; border: 1px solid var(--border); box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.18); }
        .pm-modal-view .pm-modal-head {
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          border-bottom: 1px solid #f1f5f9;
        }
        .pm-modal-icon-view {
          background: var(--link-hover-bg); border: 1px solid #dbeafe; color: #2563eb;
        }
        .pm-modal-icon-view svg { width: 20px; height: 20px; }
        .pm-view-student-card {
          display: flex; align-items: center; gap: 14px;
          padding: 16px; border-radius: 14px;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
          border: 1px solid #e2e8f0;
        }
        .pm-view-student-av {
          width: 48px; height: 48px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 15px; font-weight: 800; color: #fff;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
        }
        .pm-view-student-name { font-size: 16px; font-weight: 700; color: var(--text); line-height: 1.25; }
        .pm-view-student-meta { font-size: 12px; color: #64748b; margin-top: 4px; line-height: 1.45; }
        .pm-view-summary-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
        }
        .pm-view-summary-card {
          padding: 14px 12px; border-radius: 12px;
          background: var(--surface); border: 1px solid #e2e8f0;
          min-width: 0;
        }
        .pm-view-summary-card.highlight {
          background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
          border-color: #a7f3d0;
        }
        .pm-view-info-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
        }
        .pm-view-info-item {
          background: var(--surface); border: 1px solid #e2e8f0;
          border-radius: 12px; padding: 14px;
        }
        .pm-view-info-label {
          font-size: 10px; font-weight: 700; color: var(--muted);
          text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;
        }
        .pm-view-info-val { font-size: 13px; font-weight: 600; color: var(--text); line-height: 1.4; }
        .pm-view-amount-val { font-size: 18px; font-weight: 800; color: #047857; letter-spacing: -0.02em; }
        .pm-view-method-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; border-radius: 999px;
          font-size: 12px; font-weight: 700;
        }
        .pm-view-method-pill.office { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
        .pm-view-method-pill.bank { background: var(--link-hover-bg); color: #1d4ed8; border: 1px solid #bfdbfe; }
        .pm-view-method-pill.momo { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
        .pm-view-section {
          border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden;
          background: var(--surface);
        }
        .pm-view-section-title {
          font-size: 11px; font-weight: 700; color: #475569;
          text-transform: uppercase; letter-spacing: 0.08em;
          display: flex; align-items: center; gap: 8px;
          padding: 12px 16px; background: var(--bg);
          border-bottom: 1px solid #f1f5f9; margin-bottom: 0;
        }
        .pm-view-section-title svg { width: 16px; height: 16px; flex-shrink: 0; color: #64748b; }
        .pm-view-section-body { padding: 16px; }
        .pm-view-section-line { display: none; }

        .pm-proof-box {
          background: var(--bg); border: 1px dashed #cbd5e1;
          border-radius: 10px; padding: 16px;
        }
        .pm-proof-text {
          font-family: ui-monospace, 'Cascadia Code', 'Courier New', monospace;
          font-size: 12px; color: #334155;
          white-space: pre-wrap; word-break: break-word; line-height: 1.65;
        }
        .pm-receipt-image {
          width: 100%; border-radius: 10px; border: 1px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
        }
        .pm-receipt-pdf {
          width: 100%; height: 300px; border-radius: 10px;
          border: 1px solid #e2e8f0; background: var(--bg);
        }
        .pm-open-link {
          display: inline-flex; align-items: center; gap: 6px; margin-top: 12px;
          padding: 9px 14px; border-radius: 8px;
          background: var(--link-hover-bg); border: 1px solid #bfdbfe;
          color: #1d4ed8; font-size: 12px; font-weight: 600; text-decoration: none;
          transition: all 0.15s;
        }
        .pm-open-link:hover { background: #dbeafe; border-color: #93c5fd; }
        .pm-open-link svg { width: 14px; height: 14px; }
        .pm-view-foot-actions { display: flex; gap: 10px; justify-content: flex-end; width: 100%; }

        /* ── Student proofs panel ── */
        .pm-modal-proofs { max-width: 640px; width: 100%; }
        .pm-proofs-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 16px;
        }
        .pm-proofs-summary-card {
          background: var(--surface2);
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 12px;
          text-align: center;
        }
        .pm-proofs-summary-card span {
          display: block;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--muted);
          margin-bottom: 4px;
        }
        .pm-proofs-summary-card strong {
          font-size: 15px;
          font-weight: 800;
          color: var(--text);
          font-variant-numeric: tabular-nums;
        }
        .pm-proofs-summary-card.due strong { color: #d97706; }
        .pm-proofs-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: min(55vh, 480px);
          overflow-y: auto;
          padding-right: 2px;
        }
        .pm-proofs-item {
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          background: var(--surface);
        }
        .pm-proofs-item-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          padding: 12px 14px;
          background: var(--surface2);
          border-bottom: 1px solid var(--border);
        }
        .pm-proofs-item-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .pm-proofs-item-amount {
          font-size: 14px;
          font-weight: 800;
          color: var(--text);
          font-variant-numeric: tabular-nums;
        }
        .pm-proofs-item-date {
          font-size: 12px;
          color: var(--text2);
        }
        .pm-proofs-item-body { padding: 12px 14px; }
        .pm-proofs-item-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--muted);
          margin-bottom: 8px;
        }
        .pm-proofs-empty {
          text-align: center;
          padding: 28px 16px;
          color: var(--text2);
          font-size: 13px;
        }
        .pm-balance-row-action {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .pm-btn-view-proofs {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          padding: 9px 12px;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--text2);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          white-space: nowrap;
          transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
        }
        .pm-btn-view-proofs:hover {
          border-color: #93c5fd;
          color: #1d4ed8;
          background: #eff6ff;
        }
        .pm-btn-view-proofs svg {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }
        .pm-btn-invoice-link {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 12px 18px; border-radius: 8px;
          background: #ecfdf5; border: 1px solid #a7f3d0;
          color: #047857; font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.15s;
        }
        .pm-btn-invoice-link:hover { background: #d1fae5; border-color: #6ee7b7; }
        .pm-btn-edit-link {
          padding: 10px 16px; border-radius: 8px; border: 1px solid #fde68a;
          background: #fffbeb; color: #b45309; font-size: 13px; font-weight: 700;
          cursor: pointer; font-family: inherit;
        }
        .pm-btn-edit-link:hover { background: #fef3c7; border-color: #fcd34d; }

        /* ── FORM BUTTONS ── */
        .pm-btn-submit {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px 24px; min-width: 148px;
          background: linear-gradient(135deg, #059669, #047857);
          color: #ffffff; border: none; border-radius: 8px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
        }
        .pm-btn-submit:hover {
          background: linear-gradient(135deg, #047857, #065f46);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(5, 150, 105, 0.28);
        }
        .pm-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; box-shadow: none; }
        .pm-btn-cancel {
          padding: 12px 20px; background: var(--surface);
          border: 1.5px solid var(--border-medium); border-radius: 8px;
          color: var(--text-medium); font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .pm-btn-cancel:hover { background: var(--surface3); color: var(--text-dark); border-color: #9ca3af; }
        .pm-btn-delete-confirm {
          padding: 12px 22px; background: rgba(248,113,113,0.12);
          border: 1px solid rgba(248,113,113,0.3); border-radius: 6px;
          color: var(--error-red); font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .pm-btn-delete-confirm:hover { background: rgba(248,113,113,0.2); }

        /* ── DELETE MODAL SPECIFIC ── */
        .pm-del-student-chip {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px;
          background: rgba(248,113,113,0.06); border: 1px solid rgba(248,113,113,0.15);
          border-radius: 6px;
        }
        .pm-del-av {
          width: 38px; height: 38px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; color: white; font-family: inherit;
        }
        .pm-del-name { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
        .pm-del-method { font-size: 12px; color: var(--text-light); }
        .pm-del-warning { font-size: 13px; color: var(--text-light); line-height: 1.6; }

        /* ── EMPTY / LOADING ── */
        .pm-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 72px 24px; gap: 12px;
        }
        .pm-empty-icon { font-size: 52px; opacity: 0.25; }
        .pm-empty-title { font-family: inherit; font-size: 18px; font-weight: 700; color: var(--text-light); }
        .pm-empty-sub { font-size: 13px; color: var(--text-light); text-align: center; }
        .pm-loading { display: flex; align-items: center; justify-content: center; padding: 72px; gap: 8px; }
        .pm-dot { width: 8px; height: 8px; border-radius: 50%; animation: pmBounce 1.2s ease-in-out infinite; }
        .pm-dot:nth-child(1) { background: var(--accent); }
        .pm-dot:nth-child(2) { background: var(--accent-orange); animation-delay: 0.2s; }
        .pm-dot:nth-child(3) { background: var(--blue); animation-delay: 0.4s; }
        @keyframes pmBounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .pm-stats { grid-template-columns: repeat(2, 1fr); }
          .pm-cohort-panel-body { grid-template-columns: 1fr; }
          .pm-cohort-section { border-right: none; border-bottom: 1px solid rgba(167, 243, 208, 0.4); }
          .pm-cohort-section:last-child { border-bottom: none; }
        }
        @media (max-width: 768px) {
          .pm-root { padding: 16px; }
          .pm-page-title { font-size: 22px; }
          .pm-stats { grid-template-columns: repeat(2, 1fr); }
          .pm-cohort-bar { flex-direction: column; align-items: stretch; }
          .pm-cohort-bar-left { flex-direction: column; align-items: stretch; }
          .pm-cohort-select { min-width: 0; width: 100%; }
          .pm-cohort-progress-wrap { text-align: left; width: 100%; }
          .pm-table-wrap { display: none; }
          .pm-balance-list { display: none; }
          .pm-cards { display: flex; }
          .pm-view-info-grid { grid-template-columns: 1fr; }
          .pm-view-summary-grid { grid-template-columns: 1fr; }
          .pm-toolbar { flex-direction: column; align-items: stretch; }
          .pm-toolbar-filters { width: 100%; }
          .pm-balance-filter { flex: 1; min-width: 0; }
          .pm-pagination { flex-direction: column; align-items: stretch; gap: 12px; }
          .pm-pagination-controls { justify-content: center; }
        }
        @media (max-width: 1100px) {
          .pm-balance-list-head,
          .pm-balance-row {
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 12px 16px;
          }
          .pm-balance-list-head-fees { display: none; }
          .pm-balance-list-head-action { display: none; }
          .pm-balance-row-fees {
            grid-column: 1 / -1;
            justify-content: flex-start;
            padding-left: 54px;
            gap: 20px;
            border-top: 1px dashed var(--border);
            padding-top: 10px;
            margin-top: -2px;
          }
          .pm-balance-row-action {
            grid-column: 2;
            grid-row: 1;
          }
          .pm-balance-fee-item { align-items: flex-start; min-width: 0; }
        }
        @media (max-width: 480px) {
          .pm-stats { grid-template-columns: 1fr 1fr; }
          .pm-toolbar { flex-direction: column; align-items: stretch; }
          .pm-search { max-width: none; }
          .pm-btn-add, .pm-btn-refresh { justify-content: center; }
          .pm-method-picker { flex-direction: column; }
        }
      `}</style>

      {successMessage && <div className="pm-toast pm-toast-s">✓ {successMessage}</div>}
      {error && <div className="pm-toast pm-toast-e">⚠ {error}</div>}

      {/* ── ADD PAYMENT MODAL ── */}
      {showForm && selectedStudentObj && (
        <div className="pm-overlay" onClick={resetForm}>
          <div className="pm-modal pm-modal-sm pm-modal-add" onClick={e => e.stopPropagation()}>
            <div className="pm-modal-head">
              <div className="pm-modal-head-left">
                <div className="pm-modal-icon pm-modal-icon-green">💳</div>
                <div>
                  <div className="pm-modal-title">Record payment</div>
                  <div className="pm-modal-sub">For {selectedStudentObj.fullName}</div>
                </div>
              </div>
              <button type="button" className="pm-modal-close" onClick={resetForm} aria-label="Close">✕</button>
            </div>

            <form onSubmit={handleCreate} style={{ display: 'contents' }}>
              <div className="pm-modal-body">
                <div className="pm-field">
                  <div className="pm-label">Student</div>
                  <div className="pm-student-preview pm-student-locked">
                    <div className="pm-preview-av" style={{ background: getAvatarColor(selectedStudentObj.fullName) }}>
                      {getInitials(selectedStudentObj.fullName)}
                    </div>
                    <div>
                      <div className="pm-preview-name">{selectedStudentObj.fullName}</div>
                      <div className="pm-preview-email">
                        {[selectedStudentObj.email, selectedStudentObj.phone].filter(Boolean).join(' · ') || 'No contact on file'}
                      </div>
                      {getBatchLabel(selectedStudentObj) !== '—' && (
                        <div className="pm-student-batch-chip">Batch: {getBatchLabel(selectedStudentObj)}</div>
                      )}
                    </div>
                  </div>
                </div>

                {paymentSummary && (
                  <div className="pm-fee-summary">
                    <div className="pm-fee-row">
                      <span>Total due</span>
                      <strong>{formatRwf(paymentSummary.totalDue)}</strong>
                    </div>
                    <div className="pm-fee-row">
                      <span>Already paid</span>
                      <strong>{formatRwf(paymentSummary.totalPaid)}</strong>
                    </div>
                    <div className="pm-fee-row pm-fee-balance">
                      <span>Remaining</span>
                      <strong>{formatRwf(paymentSummary.balance)}</strong>
                    </div>
                  </div>
                )}

                <div className="pm-field">
                  <label className="pm-label" htmlFor="pm-amount">Amount (RWF)</label>
                  <input
                    id="pm-amount"
                    className="pm-input"
                    type="number"
                    name="amount"
                    min="1"
                    max={paymentSummary?.balance > 0 ? paymentSummary.balance : undefined}
                    step="1"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter amount"
                    required
                  />
                  {paymentSummary && paymentSummary.balance > 0 && (
                    <div className="pm-amount-quick">
                      <button
                        type="button"
                        onClick={() => setFormData((p) => ({ ...p, amount: String(paymentSummary.balance) }))}
                      >
                        Pay remaining ({formatRwf(paymentSummary.balance)})
                      </button>
                      {paymentSummary.balance > 1000 && (
                        <button
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, amount: String(Math.round(paymentSummary.balance / 2)) }))}
                        >
                          Half ({formatRwf(Math.round(paymentSummary.balance / 2))})
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="pm-field">
                  <label className="pm-label">Payment method</label>
                  <div className="pm-method-picker" role="radiogroup" aria-label="Payment method">
                    <button
                      type="button"
                      className={`pm-method-opt${formData.method === 'office' ? ' active-office' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, method: 'office' }))}
                      aria-pressed={formData.method === 'office'}
                    >
                      <span className="pm-method-opt-icon">🏫</span>
                      <span className="pm-method-opt-label">At office</span>
                    </button>
                    <button
                      type="button"
                      className={`pm-method-opt${formData.method === 'bank_transfer' ? ' active-bank' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, method: 'bank_transfer' }))}
                      aria-pressed={formData.method === 'bank_transfer'}
                    >
                      <span className="pm-method-opt-icon">🏦</span>
                      <span className="pm-method-opt-label">Bank</span>
                    </button>
                    <button
                      type="button"
                      className={`pm-method-opt${formData.method === 'mobile_money' ? ' active-momo' : ''}`}
                      onClick={() => setFormData(p => ({ ...p, method: 'mobile_money' }))}
                      aria-pressed={formData.method === 'mobile_money'}
                    >
                      <span className="pm-method-opt-icon">📱</span>
                      <span className="pm-method-opt-label">MoMo</span>
                    </button>
                  </div>
                  <p className="pm-field-hint">
                    {formData.method === 'office'
                      ? 'Fastest option when a student pays cash at the office after registering online.'
                      : formData.method === 'bank_transfer'
                        ? 'Upload a receipt file or enter a reference for the bank deposit.'
                        : 'Paste the MoMo confirmation message. Each installment keeps its own message — nothing is overwritten.'}
                  </p>
                </div>

                {formData.method === 'office' && (
                  <div className="pm-field">
                    <label className="pm-label" htmlFor="pm-office-note">Note (optional)</label>
                    <input
                      id="pm-office-note"
                      className="pm-input"
                      type="text"
                      name="officeNote"
                      value={formData.officeNote}
                      onChange={handleInputChange}
                      placeholder="e.g. Cash — received at front desk"
                    />
                  </div>
                )}

                {formData.method === 'bank_transfer' && (
                  <>
                    <div className="pm-field">
                      <label className="pm-label">Upload receipt (optional)</label>
                      <label className={`pm-file-upload${receiptFile ? ' has-file' : ''}`}>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                        />
                        <div className="pm-file-upload-label">
                          {receiptFile ? '📎 Receipt selected' : '⬆ Click to upload receipt'}
                        </div>
                        {receiptFile && <div className="pm-file-upload-name">{receiptFile.name}</div>}
                      </label>
                    </div>
                    <div className="pm-field">
                      <label className="pm-label" htmlFor="pm-receipt-path">Or type reference</label>
                      <input
                        id="pm-receipt-path"
                        className="pm-input"
                        type="text"
                        name="receiptPath"
                        value={formData.receiptPath}
                        onChange={handleInputChange}
                        placeholder="e.g. receipt_123.pdf or manual reference"
                        disabled={!!receiptFile}
                      />
                    </div>
                  </>
                )}

                {formData.method === 'mobile_money' && (
                  <div className="pm-field">
                    <label className="pm-label" htmlFor="pm-payment-proof">Payment proof</label>
                    <textarea
                      id="pm-payment-proof"
                      className="pm-textarea"
                      name="paymentProof"
                      value={formData.paymentProof}
                      onChange={handleInputChange}
                      placeholder="Paste the MoMo / payment confirmation message..."
                    />
                  </div>
                )}
              </div>

              <div className="pm-modal-foot">
                <button type="button" className="pm-btn-cancel" onClick={resetForm}>Cancel</button>
                <button type="submit" className="pm-btn-submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ACTIONS MENU MODAL ── */}
      {actionMenuPayment && (
        <div className="pm-overlay" onClick={() => setActionMenuPayment(null)}>
          <div className="pm-actions-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="pm-actions-title">
            <div className="pm-actions-menu-head">
              <div className="pm-actions-menu-head-main">
                <div
                  className="pm-actions-menu-av"
                  style={{ background: getAvatarColor(getStudentName(actionMenuPayment)) }}
                >
                  {getInitials(getStudentName(actionMenuPayment))}
                </div>
                <div className="pm-actions-menu-head-text">
                  <div className="pm-actions-menu-eyebrow">Payment actions</div>
                  <div className="pm-actions-menu-title" id="pm-actions-title">{getStudentName(actionMenuPayment)}</div>
                  <div className="pm-actions-menu-meta">
                    {actionMenuPayment.amount > 0 && (
                      <span className="pm-actions-menu-amount">{formatRwf(actionMenuPayment.amount)}</span>
                    )}
                    <span className="pm-actions-menu-method">{getPaymentMethod(actionMenuPayment)}</span>
                    {actionMenuPayment.createdAt && (
                      <span className="pm-actions-menu-date">
                        {new Date(actionMenuPayment.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button type="button" className="pm-actions-menu-close" onClick={() => setActionMenuPayment(null)} aria-label="Close">✕</button>
            </div>
            <div className="pm-actions-menu-body">
              <div className="pm-actions-menu-label">Choose an action</div>
              <div className="pm-actions-menu-list">
                <button
                  type="button"
                  className="pm-actions-menu-item"
                  onClick={() => { setViewPayment(actionMenuPayment); setActionMenuPayment(null); }}
                >
                  <span className="pm-actions-menu-item-icon view" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                  <span className="pm-actions-menu-item-text">
                    <span className="pm-actions-menu-item-title">View payment</span>
                    <span className="pm-actions-menu-item-desc">See proof, method, and payment details</span>
                  </span>
                  <span className="pm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
                <button
                  type="button"
                  className="pm-actions-menu-item"
                  onClick={() => openEditPayment(actionMenuPayment)}
                >
                  <span className="pm-actions-menu-item-icon edit" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </span>
                  <span className="pm-actions-menu-item-text">
                    <span className="pm-actions-menu-item-title">Edit payment proof</span>
                    <span className="pm-actions-menu-item-desc">Update MoMo message, bank reference, or office note</span>
                  </span>
                  <span className="pm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
                <button
                  type="button"
                  className="pm-actions-menu-item"
                  onClick={() => { generateReceiptFor(actionMenuPayment); setActionMenuPayment(null); }}
                >
                  <span className="pm-actions-menu-item-icon invoice" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </span>
                  <span className="pm-actions-menu-item-text">
                    <span className="pm-actions-menu-item-title">Receipt (PDF)</span>
                    <span className="pm-actions-menu-item-desc">Generate and download student payment receipt</span>
                  </span>
                  <span className="pm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
              </div>
              <div className="pm-actions-menu-divider" />
              <div className="pm-actions-menu-list">
                <button
                  type="button"
                  className="pm-actions-menu-item danger"
                  onClick={() => { setDeleteConfirm(actionMenuPayment); setActionMenuPayment(null); }}
                >
                  <span className="pm-actions-menu-item-icon delete" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </span>
                  <span className="pm-actions-menu-item-text">
                    <span className="pm-actions-menu-item-title">Delete payment</span>
                    <span className="pm-actions-menu-item-desc">Remove record and recalculate balance</span>
                  </span>
                  <span className="pm-actions-menu-item-chevron" aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT PAYMENT MODAL ── */}
      {editPayment && (
        <div className="pm-overlay" onClick={closeEditPayment}>
          <div className="pm-modal pm-modal-sm pm-modal-add" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-head">
              <div className="pm-modal-head-left">
                <div className="pm-modal-icon pm-modal-icon-green">✏️</div>
                <div>
                  <div className="pm-modal-title">Edit payment proof</div>
                  <div className="pm-modal-sub">{getStudentName(editPayment)} · {getPaymentMethod(editPayment)}</div>
                </div>
              </div>
              <button type="button" className="pm-modal-close" onClick={closeEditPayment} aria-label="Close">✕</button>
            </div>
            <form onSubmit={handleEditSave} style={{ display: 'contents' }}>
              <div className="pm-modal-body">
                <div className="pm-fee-summary" style={{ marginBottom: 16 }}>
                  <div className="pm-fee-row">
                    <span>Amount</span>
                    <strong>{editPayment.amount ? formatRwf(editPayment.amount) : '—'}</strong>
                  </div>
                  <div className="pm-fee-row">
                    <span>Date</span>
                    <strong>
                      {editPayment.createdAt
                        ? new Date(editPayment.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : '—'}
                    </strong>
                  </div>
                </div>

                {getMethodType(editPayment) === 'momo' && (
                  <div className="pm-field">
                    <label className="pm-label" htmlFor="pm-edit-payment-proof">Mobile money proof</label>
                    <textarea
                      id="pm-edit-payment-proof"
                      className="pm-textarea"
                      name="paymentProof"
                      value={editFormData.paymentProof}
                      onChange={handleEditInputChange}
                      placeholder="Paste the MoMo / payment confirmation message..."
                      required
                    />
                  </div>
                )}

                {getMethodType(editPayment) === 'bank' && (
                  <div className="pm-field">
                    <label className="pm-label" htmlFor="pm-edit-receipt-path">Bank receipt reference</label>
                    <input
                      id="pm-edit-receipt-path"
                      className="pm-input"
                      type="text"
                      name="receiptPath"
                      value={editFormData.receiptPath}
                      onChange={handleEditInputChange}
                      placeholder="e.g. receipt_123.pdf or deposit reference"
                      required
                    />
                  </div>
                )}

                {getMethodType(editPayment) === 'office' && (
                  <div className="pm-field">
                    <label className="pm-label" htmlFor="pm-edit-office-note">Office payment note</label>
                    <input
                      id="pm-edit-office-note"
                      className="pm-input"
                      type="text"
                      name="officeNote"
                      value={editFormData.officeNote}
                      onChange={handleEditInputChange}
                      placeholder="e.g. Cash — received at front desk"
                    />
                  </div>
                )}
              </div>
              <div className="pm-modal-foot">
                <button type="button" className="pm-btn-cancel" onClick={closeEditPayment}>Cancel</button>
                <button type="submit" className="pm-btn-submit" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── VIEW PAYMENT MODAL ── */}
      {viewPayment && (
        <div className="pm-overlay" onClick={() => setViewPayment(null)}>
          <div className="pm-modal pm-modal-lg pm-modal-view" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="pm-view-title">
            <div className="pm-modal-head">
              <div className="pm-modal-head-left">
                <div className="pm-modal-icon pm-modal-icon-view" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </div>
                <div>
                  <div className="pm-modal-title" id="pm-view-title">Payment Verification</div>
                  <div className="pm-modal-sub">Review payment details and proof</div>
                </div>
              </div>
              <button type="button" className="pm-modal-close" onClick={() => setViewPayment(null)} aria-label="Close">✕</button>
            </div>

            <div className="pm-modal-body">
              <div className="pm-view-student-card">
                <div
                  className="pm-view-student-av"
                  style={{ background: getAvatarColor(getStudentName(viewPayment)) }}
                >
                  {getInitials(getStudentName(viewPayment))}
                </div>
                <div>
                  <div className="pm-view-student-name">{getStudentName(viewPayment)}</div>
                  <div className="pm-view-student-meta">
                    {getStudentEmail(viewPayment)}
                    {getStudentPhone(viewPayment) !== '—' && getStudentPhone(viewPayment) !== getStudentEmail(viewPayment) && (
                      <> · {getStudentPhone(viewPayment)}</>
                    )}
                  </div>
                </div>
              </div>

              <div className="pm-view-summary-grid">
                <div className="pm-view-summary-card highlight">
                  <div className="pm-view-info-label">Amount</div>
                  <div className="pm-view-amount-val">
                    {viewPayment.amount ? formatRwf(viewPayment.amount) : '—'}
                  </div>
                </div>
                <div className="pm-view-summary-card">
                  <div className="pm-view-info-label">Method</div>
                  <div className="pm-view-info-val">
                    <span className={`pm-view-method-pill ${getViewMethodPillClass(viewPayment)}`}>
                      {getMethodIcon(viewPayment)} {getPaymentMethod(viewPayment)}
                    </span>
                  </div>
                </div>
                <div className="pm-view-summary-card">
                  <div className="pm-view-info-label">Date</div>
                  <div className="pm-view-info-val">
                    {viewPayment.createdAt
                      ? new Date(viewPayment.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                      : '—'}
                  </div>
                </div>
              </div>

              {viewPayment.receiptPath && (() => {
                const proof = getViewProofSection(viewPayment);
                return (
                  <div className="pm-view-section">
                    <div className="pm-view-section-title">
                      {proof.type === 'bank' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
                      ) : proof.type === 'momo' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12" y2="18" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                      )}
                      {proof.title}
                    </div>
                    <div className="pm-view-section-body">
                      {viewPayment.receiptPath.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                        <>
                          <img
                            src={paymentService.getReceiptUrl(viewPayment.receiptPath)}
                            alt="Payment receipt"
                            className="pm-receipt-image"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <a href={paymentService.getReceiptUrl(viewPayment.receiptPath)} target="_blank" rel="noopener noreferrer" className="pm-open-link">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                            Open in new tab
                          </a>
                        </>
                      ) : viewPayment.receiptPath.match(/\.pdf$/i) ? (
                        <>
                          <iframe title="Receipt" src={paymentService.getReceiptUrl(viewPayment.receiptPath)} className="pm-receipt-pdf" />
                          <a href={paymentService.getReceiptUrl(viewPayment.receiptPath)} target="_blank" rel="noopener noreferrer" className="pm-open-link">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                            Open PDF
                          </a>
                        </>
                      ) : (
                        <>
                          <div className="pm-proof-box">
                            <div className="pm-proof-text">{viewPayment.receiptPath}</div>
                          </div>
                          {!viewPayment.receiptPath.includes('Office payment') && (
                            <a href={paymentService.getReceiptUrl(viewPayment.receiptPath)} target="_blank" rel="noopener noreferrer" className="pm-open-link">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                              Try open file
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {viewPayment.paymentProof && (
                <div className="pm-view-section">
                  <div className="pm-view-section-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12" y2="18" /></svg>
                    Mobile Money Confirmation
                  </div>
                  <div className="pm-view-section-body">
                    <div className="pm-proof-box">
                      <div className="pm-proof-text">{viewPayment.paymentProof}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="pm-modal-foot">
              <div className="pm-view-foot-actions">
                <button
                  type="button"
                  className="pm-btn-edit-link"
                  onClick={() => openEditPayment(viewPayment)}
                >
                  Edit proof
                </button>
                <button
                  type="button"
                  className="pm-btn-invoice-link"
                  onClick={() => generateReceiptFor(viewPayment)}
                  disabled={generatingReceiptId === viewPayment._id}
                >
                  {generatingReceiptId === viewPayment._id ? 'Generating…' : 'Generate receipt'}
                </button>
                <button type="button" className="pm-btn-cancel" onClick={() => setViewPayment(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STUDENT PROOFS PANEL ── */}
      {studentProofs && (
        <div className="pm-overlay" onClick={closeStudentProofs}>
          <div className="pm-modal pm-modal-proofs" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="pm-proofs-title">
            <div className="pm-modal-head">
              <div className="pm-modal-head-left">
                <div
                  className="pm-modal-icon"
                  style={{ background: getAvatarColor(studentProofs.student.fullName), color: '#fff', fontWeight: 800, fontSize: 14 }}
                >
                  {getInitials(studentProofs.student.fullName)}
                </div>
                <div>
                  <div className="pm-modal-title" id="pm-proofs-title">Payment proofs</div>
                  <div className="pm-modal-sub">{studentProofs.student.fullName}</div>
                </div>
              </div>
              <button type="button" className="pm-modal-close" onClick={closeStudentProofs} aria-label="Close">✕</button>
            </div>
            <div className="pm-modal-body">
              {studentProofs.summary && (
                <div className="pm-proofs-summary">
                  <div className="pm-proofs-summary-card">
                    <span>Due</span>
                    <strong>{formatRwf(studentProofs.summary.totalDue)}</strong>
                  </div>
                  <div className="pm-proofs-summary-card">
                    <span>Paid</span>
                    <strong>{formatRwf(studentProofs.summary.totalPaid)}</strong>
                  </div>
                  <div className="pm-proofs-summary-card due">
                    <span>Balance</span>
                    <strong>{formatRwf(studentProofs.summary.balance)}</strong>
                  </div>
                </div>
              )}

              {studentProofsLoading ? (
                <div className="pm-loading" style={{ padding: 32 }}>
                  <div className="pm-dot" /><div className="pm-dot" /><div className="pm-dot" />
                </div>
              ) : studentProofs.payments.length === 0 ? (
                <div className="pm-proofs-empty">No payments recorded for this student yet.</div>
              ) : (
                <div className="pm-proofs-list">
                  {studentProofs.payments.map((p) => {
                    const methodType = getMethodType(p);
                    const proofLabel = methodType === 'momo'
                      ? 'MoMo confirmation message'
                      : methodType === 'bank'
                        ? 'Bank receipt / reference'
                        : 'Office note';
                    const textProof = p.paymentProof
                      || (p.receiptPath && !/\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(p.receiptPath) ? p.receiptPath : null)
                      || p.note
                      || null;
                    const fileProof = p.receiptPath && /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(p.receiptPath)
                      ? p.receiptPath
                      : null;
                    return (
                      <div key={p._id} className="pm-proofs-item">
                        <div className="pm-proofs-item-head">
                          <div className="pm-proofs-item-meta">
                            <span className="pm-proofs-item-amount">{p.amount ? formatRwf(p.amount) : '—'}</span>
                            <span className={`pm-badge ${getMethodBadgeClass(p)}`}>
                              {getMethodIcon(p)} {getPaymentMethod(p)}
                            </span>
                          </div>
                          <span className="pm-proofs-item-date">
                            {p.createdAt
                              ? new Date(p.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                              : '—'}
                          </span>
                        </div>
                        <div className="pm-proofs-item-body">
                          <div className="pm-proofs-item-label">{proofLabel}</div>
                          {textProof && (
                            <div className="pm-proof-box">
                              <div className="pm-proof-text">{textProof}</div>
                            </div>
                          )}
                          {fileProof && (
                            <div style={{ marginTop: textProof ? 10 : 0 }}>
                              {/\.(jpg|jpeg|png|gif|webp)$/i.test(fileProof) ? (
                                <img
                                  src={paymentService.getReceiptUrl(fileProof)}
                                  alt="Payment receipt"
                                  className="pm-receipt-image"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <a
                                  href={paymentService.getReceiptUrl(fileProof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="pm-open-link"
                                >
                                  Open receipt file
                                </a>
                              )}
                            </div>
                          )}
                          {!textProof && !fileProof && (
                            <div className="pm-proofs-empty" style={{ padding: '8px 0' }}>No proof text saved on this payment.</div>
                          )}
                          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="pm-btn-view-proofs"
                              onClick={() => { setViewPayment(p); closeStudentProofs(); }}
                            >
                              Open details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="pm-modal-foot">
              {studentProofs.summary?.status !== 'paid' && studentProofs.summary?.totalDue > 0 && (
                <button
                  type="button"
                  className="pm-btn-record-balance"
                  onClick={() => {
                    const id = studentProofs.student._id;
                    closeStudentProofs();
                    openPaymentForStudent(id);
                  }}
                >
                  Record another payment
                </button>
              )}
              <button type="button" className="pm-btn-cancel" onClick={closeStudentProofs}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteConfirm && (
        <div className="pm-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="pm-modal pm-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="pm-modal-head">
              <div className="pm-modal-head-left">
                <div className="pm-modal-icon pm-modal-icon-red">🗑️</div>
                <div>
                  <div className="pm-modal-title">Remove Payment?</div>
                  <div className="pm-modal-sub">This action cannot be undone</div>
                </div>
              </div>
              <button className="pm-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-del-student-chip">
                <div className="pm-del-av" style={{ background: getAvatarColor(getStudentName(deleteConfirm)) }}>
                  {getInitials(getStudentName(deleteConfirm))}
                </div>
                <div>
                  <div className="pm-del-name">{getStudentName(deleteConfirm)}</div>
                  <div className="pm-del-method">{getPaymentMethod(deleteConfirm)} · {deleteConfirm.createdAt ? new Date(deleteConfirm.createdAt).toLocaleDateString() : '—'}</div>
                </div>
              </div>
              <div className="pm-del-warning">
                This will permanently remove the payment record for <strong>{getStudentName(deleteConfirm)}</strong>. The student&apos;s balance will be recalculated.
              </div>
            </div>
            <div className="pm-modal-foot">
              <button className="pm-btn-cancel" onClick={() => setDeleteConfirm(null)}>Keep Payment</button>
              <button className="pm-btn-delete-confirm" onClick={handleDeleteConfirmed}>Remove</button>
            </div>
          </div>
        </div>
      )}

      <div className="pm-root">
        {/* Page Header */}
        <div className="pm-page-header">
          <div>
            <div className="pm-eyebrow">✦ Finance</div>
            <h1 className="pm-page-title">Payment <span>Management</span></h1>
            <p className="pm-page-sub">Record payments after registration — office, bank, or mobile money</p>
          </div>
        </div>

        {/* Cohort selector */}
        <div className="pm-cohort-bar">
          <div className="pm-cohort-bar-left">
            <div className="pm-cohort-bar-icon" aria-hidden="true">🎓</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pm-cohort-bar-label">Track payments by batch</div>
              <select
                className="pm-cohort-select"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                aria-label="Batch filter"
              >
                <option value="">All batches — overview</option>
                <option value="__none__">No batch assigned</option>
                {sortedBatches.map((batch) => (
                  <option key={batch._id} value={batch._id}>
                    {batch.name} ({batch.track === 'professional' ? 'Professional' : 'Secondary'}
                    {batch.status === 'running' ? ' · Running' : ''})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="pm-cohort-bar-actions">
            <button className="pm-btn-refresh" onClick={() => { fetchPayments(); fetchBalances(); fetchBatches(); }}>↻ Refresh</button>
          </div>
        </div>

        {batchFilter ? (
          <div className="pm-cohort-panel">
            <div className="pm-cohort-panel-head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pm-cohort-panel-title">{selectedBatchName}</div>
                <div className="pm-cohort-panel-meta">
                  {selectedBatchObj && (
                    <span className="pm-cohort-badge track">
                      {selectedBatchObj.track === 'professional' ? 'Professional' : 'Secondary'}
                    </span>
                  )}
                  {selectedBatchObj?.status === 'running' && (
                    <span className="pm-cohort-badge running">● Running</span>
                  )}
                  {batchFilter === '__none__' && (
                    <span className="pm-cohort-badge dates">Unassigned students</span>
                  )}
                  {selectedBatchObj && formatBatchDates(selectedBatchObj) && (
                    <span className="pm-cohort-badge dates">{formatBatchDates(selectedBatchObj)}</span>
                  )}
                </div>
                <div className="pm-cohort-progress-bar" style={{ maxWidth: 320, marginTop: 14 }}>
                  <div
                    className="pm-cohort-progress-fill"
                    style={{ width: `${batchSummary.collectionRate}%` }}
                  />
                </div>
              </div>
              <div className="pm-cohort-progress-wrap">
                <div className="pm-cohort-progress-pct">{batchSummary.collectionRate}%</div>
                <div className="pm-cohort-progress-lbl">Collection rate</div>
              </div>
            </div>
            <div className="pm-cohort-panel-body">
              <div className="pm-cohort-section">
                <div className="pm-cohort-section-title">Student status</div>
                <div className="pm-cohort-status-list">
                  <div className="pm-cohort-status-row">
                    <span className="pm-cohort-status-label">
                      <span className="pm-cohort-status-dot paid" /> Fully paid
                    </span>
                    <span className="pm-cohort-status-val">{batchSummary.paid}</span>
                  </div>
                  <div className="pm-cohort-status-row">
                    <span className="pm-cohort-status-label">
                      <span className="pm-cohort-status-dot partial" /> Partial
                    </span>
                    <span className="pm-cohort-status-val">{batchSummary.partial}</span>
                  </div>
                  <div className="pm-cohort-status-row">
                    <span className="pm-cohort-status-label">
                      <span className="pm-cohort-status-dot unpaid" /> Unpaid
                    </span>
                    <span className="pm-cohort-status-val">{batchSummary.unpaid}</span>
                  </div>
                </div>
                <div className="pm-cohort-money-sub" style={{ marginTop: 14 }}>
                  <strong>{batchSummary.students}</strong> student{batchSummary.students !== 1 ? 's' : ''} in this cohort
                </div>
              </div>
              <div className="pm-cohort-section">
                <div className="pm-cohort-section-title">Total received</div>
                <div className="pm-cohort-money-val accent">{formatRwf(batchSummary.totalPaid)}</div>
                <div className="pm-cohort-money-sub">Includes full and partial payments from this batch</div>
              </div>
              <div className="pm-cohort-section">
                <div className="pm-cohort-section-title">Still owed</div>
                <div className={`pm-cohort-money-val${batchSummary.outstanding > 0 ? ' warn' : ''}`}>
                  {formatRwf(batchSummary.outstanding)}
                </div>
                <div className="pm-cohort-money-sub">
                  of {formatRwf(batchSummary.totalDue)} total fees expected
                </div>
              </div>
            </div>
            <div className="pm-cohort-panel-foot">
              <span>
                <strong>{batchSummary.txCount}</strong> transaction{batchSummary.txCount !== 1 ? 's' : ''} recorded
                {batchSummary.txTotal > 0 && <> · {formatRwf(batchSummary.txTotal)} in payment history</>}
              </span>
              <span>
                {batchSummary.paid + batchSummary.partial + batchSummary.unpaid > 0
                  ? `${Math.round((batchSummary.paid / batchSummary.students) * 100) || 0}% fully paid`
                  : 'No students in filter'}
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="pm-stats">
              <div className="pm-stat" style={{ '--c': '#10B981', '--bg-i': 'rgba(16,185,129,0.12)' }}>
                <div className="pm-stat-icon">💰</div>
                <div className="pm-stat-body">
                  <div className="pm-stat-num">{displayPaymentCount}</div>
                  <div className="pm-stat-lbl">Total payments</div>
                </div>
              </div>
              <div className="pm-stat" style={{ '--c': '#F59E0B', '--bg-i': 'rgba(245,158,11,0.12)' }}>
                <div className="pm-stat-icon">📱</div>
                <div className="pm-stat-body">
                  <div className="pm-stat-num">{momoCount + officeCount + bankCount}</div>
                  <div className="pm-stat-lbl">Recorded transactions</div>
                </div>
              </div>
              <div className="pm-stat" style={{ '--c': '#F472B6', '--bg-i': 'rgba(244,114,182,0.12)' }}>
                <div className="pm-stat-icon">⏳</div>
                <div className="pm-stat-body">
                  <div className="pm-stat-num">{formatRwf(displayOutstanding)}</div>
                  <div className="pm-stat-lbl">Total owed</div>
                </div>
              </div>
              <div className="pm-stat" style={{ '--c': '#8B5CF6', '--bg-i': 'rgba(139,92,246,0.12)' }}>
                <div className="pm-stat-icon">📊</div>
                <div className="pm-stat-body">
                  <div className="pm-stat-num">{displayPartialCount}</div>
                  <div className="pm-stat-lbl">Partial payments</div>
                </div>
              </div>
            </div>
            <div className="pm-batch-hint">
              <div className="pm-batch-hint-icon" aria-hidden="true">🎯</div>
              <div>
                <div className="pm-batch-hint-title">Filter by batch for cohort tracking</div>
                <div className="pm-batch-hint-text">
                  Select a batch above to see collection progress, student payment status, and batch-scoped history.
                </div>
              </div>
            </div>
          </>
        )}

        <div className="pm-view-tabs">
          <button
            type="button"
            className={`pm-view-tab${listView === 'balances' ? ' active' : ''}`}
            onClick={() => setListView('balances')}
          >
            Student balances ({owingCount} owing)
          </button>
          <button
            type="button"
            className={`pm-view-tab${listView === 'history' ? ' active' : ''}`}
            onClick={() => setListView('history')}
          >
            Payment history ({filteredPayments.length})
          </button>
        </div>

        <div className="pm-toolbar-card">
        <div className="pm-toolbar">
          <div className="pm-search">
            <span className="pm-search-icon">🔍</span>
            <input
              type="text"
              placeholder={listView === 'balances' ? 'Search students by name, email, phone, or program...' : 'Search by student name or email...'}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="pm-clear" onClick={() => setSearchTerm('')}>✕</button>}
          </div>
          <div className="pm-toolbar-filters">
            {listView === 'history' && (
              <label className="pm-group-toggle">
                <input
                  type="checkbox"
                  checked={groupHistoryByBatch}
                  onChange={(e) => setGroupHistoryByBatch(e.target.checked)}
                />
                Group by batch
              </label>
            )}
            {listView === 'balances' && (
              <>
                <select
                  className="pm-balance-filter"
                  value={balanceStatusFilter}
                  onChange={(e) => setBalanceStatusFilter(e.target.value)}
                  aria-label="Balance status filter"
                >
                  {BALANCE_STATUS_FILTERS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {filteredOutstandingRwf > 0 && (
                  <span className="pm-outstanding-total">
                    Outstanding: {formatRwf(filteredOutstandingRwf)} RWF
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        </div>

        <div className="pm-section-head">
          <div className="pm-section-title">
            <span className="pm-section-dot" />
            {listView === 'balances' ? 'Student balances' : 'Payment history'}
            <span className="pm-count-badge">
              {listView === 'balances' ? filteredBalanceRows.length : filteredPayments.length}
            </span>
          </div>
          {searchTerm && <span className="pm-search-hint">Results for "<span>{searchTerm}</span>"</span>}
        </div>

        {loading ? (
          <div className="pm-loading"><div className="pm-dot"/><div className="pm-dot"/><div className="pm-dot"/></div>
        ) : listView === 'balances' ? (
          filteredBalanceRows.length === 0 ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px' }}>
              <div className="pm-empty">
                <span className="pm-empty-icon">📊</span>
                <div className="pm-empty-title">{searchTerm || balanceStatusFilter !== '' ? 'No matching students' : 'All students are fully paid'}</div>
                <div className="pm-empty-sub">
                  {balanceStatusFilter === 'outstanding'
                    ? 'Switch to “All students” to see everyone, or check Payment history for past transactions.'
                    : 'Try adjusting your search or status filter.'}
                </div>
              </div>
            </div>
          ) : (
            <div className="pm-balances-card">
              <div className="pm-balances-card-head">
                <div>
                  <div className="pm-balances-card-title">Outstanding student fees</div>
                  <div className="pm-balances-card-meta">
                    <strong>{filteredBalanceRows.length}</strong> student{filteredBalanceRows.length === 1 ? '' : 's'}
                    {filteredOutstandingRwf > 0 && (
                      <> · <strong>{formatRwf(filteredOutstandingRwf)}</strong> RWF still to collect</>
                    )}
                  </div>
                </div>
                {filteredOutstandingRwf > 0 && (
                  <span className="pm-balances-outstanding-pill">
                    <span>To collect</span>
                    {formatRwf(filteredOutstandingRwf)} RWF
                  </span>
                )}
              </div>

              <div className="pm-balance-list">
                <div className="pm-balance-list-head">
                  <span>Student</span>
                  <div className="pm-balance-list-head-fees">
                    <span>Due</span>
                    <span>Paid</span>
                    <span>Balance</span>
                  </div>
                  <span className="pm-balance-list-head-action">Action</span>
                </div>

                {paginatedBalanceRows.map(({ student, balance }, i) => {
                  const batchLabel = getBatchLabel(student);
                  const hasBatch = batchLabel && batchLabel !== '—';
                  const program = getProgramLabel(student);
                  const canRecord = balance.status !== 'paid' && balance.totalDue > 0;
                  return (
                    <div
                      key={student._id}
                      className={`pm-balance-row status-${balance.status}`}
                      style={{ animationDelay: `${i * 30}ms` }}
                    >
                      <div className="pm-balance-row-student">
                        <div className="pm-balance-ava" style={{ background: getAvatarColor(student.fullName) }}>
                          {getInitials(student.fullName)}
                        </div>
                        <div className="pm-balance-student-body">
                          <div className="pm-balance-student-top">
                            <div className="pm-balance-student-name" title={student.fullName}>{student.fullName}</div>
                            {getBalanceStatusBadge(balance.status)}
                          </div>
                          <div className="pm-balance-student-program" title={program}>{program}</div>
                          {hasBatch && <span className="pm-balance-batch-chip">{batchLabel}</span>}
                        </div>
                      </div>

                      <div className="pm-balance-row-fees">
                        <div className="pm-balance-fee-item">
                          <span className="pm-balance-fee-lbl">Due</span>
                          <span className="pm-balance-fee-val muted">
                            {balance.totalDue > 0 ? formatRwf(balance.totalDue) : '—'}
                          </span>
                        </div>
                        <div className="pm-balance-fee-item">
                          <span className="pm-balance-fee-lbl">Paid</span>
                          <span className="pm-balance-fee-val">{formatRwf(balance.totalPaid)}</span>
                        </div>
                        <div className="pm-balance-fee-item outstanding">
                          <span className="pm-balance-fee-lbl">Balance</span>
                          <span className={`pm-balance-fee-val${balance.balance > 0 ? ' due' : ' clear'}`}>
                            {balance.balance > 0 ? formatRwf(balance.balance) : '0'}
                          </span>
                        </div>
                      </div>

                      <div className="pm-balance-row-action">
                        {(balance.paymentCount > 0 || balance.totalPaid > 0) && (
                          <button
                            type="button"
                            className="pm-btn-view-proofs"
                            onClick={() => openStudentProofs(student)}
                            title="View all payment proofs"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            Proofs
                          </button>
                        )}
                        {canRecord ? (
                          <button type="button" className="pm-btn-record-balance" onClick={() => openPaymentForStudent(student._id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Record payment
                          </button>
                        ) : (
                          <span className="pm-balance-settled">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Settled
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pm-cards">
                {paginatedBalanceRows.map(({ student, balance }, i) => (
                  <div key={student._id} className="pm-card" style={{ animationDelay: `${i * 40}ms` }}>
                    <div className="pm-card-head">
                      <div className="pm-card-av" style={{ background: getAvatarColor(student.fullName) }}>
                        {getInitials(student.fullName)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="pm-card-name">{student.fullName}</div>
                        <div className="pm-card-email">{getProgramLabel(student)}</div>
                      </div>
                      {getBalanceStatusBadge(balance.status)}
                    </div>
                    <div className="pm-card-body pm-card-fees-strip">
                      <div className="pm-card-fee">
                        <span className="pm-card-fee-lbl">Due</span>
                        <span className="pm-card-fee-val">{balance.totalDue > 0 ? formatRwf(balance.totalDue) : '—'}</span>
                      </div>
                      <div className="pm-card-fee">
                        <span className="pm-card-fee-lbl">Paid</span>
                        <span className="pm-card-fee-val">{formatRwf(balance.totalPaid)}</span>
                      </div>
                      <div className="pm-card-fee highlight">
                        <span className="pm-card-fee-lbl">Balance</span>
                        <span className="pm-card-fee-val">{balance.balance > 0 ? formatRwf(balance.balance) : '0'}</span>
                      </div>
                    </div>
                    {(balance.paymentCount > 0 || balance.totalPaid > 0 || (balance.status !== 'paid' && balance.totalDue > 0)) && (
                    <div className="pm-card-foot" style={{ flexDirection: 'column', gap: 8 }}>
                      {(balance.paymentCount > 0 || balance.totalPaid > 0) && (
                        <button
                          type="button"
                          className="pm-btn-view-proofs"
                          style={{ width: '100%' }}
                          onClick={() => openStudentProofs(student)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          View all proofs
                        </button>
                      )}
                      {balance.status !== 'paid' && balance.totalDue > 0 && (
                        <button type="button" className="pm-btn-record-balance" style={{ width: '100%' }} onClick={() => openPaymentForStudent(student._id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                          Record payment
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="pm-pagination">
                <div className="pm-pagination-info">
                  Showing <strong>{balanceRangeStart}–{balanceRangeEnd}</strong> of <strong>{filteredBalanceRows.length}</strong> students
                </div>
                <div className="pm-pagination-controls">
                  <select
                    className="pm-pagination-size"
                    value={balancePageSize}
                    onChange={(e) => { setBalancePageSize(Number(e.target.value)); setBalancePage(1); }}
                    aria-label="Rows per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} / page</option>
                    ))}
                  </select>
                  <button type="button" className="pm-pagination-btn" disabled={safeBalancePage <= 1} onClick={() => setBalancePage((p) => Math.max(1, p - 1))}>← Prev</button>
                  <span className="pm-pagination-page">Page {safeBalancePage} of {balanceTotalPages}</span>
                  <button type="button" className="pm-pagination-btn" disabled={safeBalancePage >= balanceTotalPages} onClick={() => setBalancePage((p) => Math.min(balanceTotalPages, p + 1))}>Next →</button>
                </div>
              </div>
            </div>
          )
        ) : filteredPayments.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px' }}>
            <div className="pm-empty">
              <span className="pm-empty-icon">💳</span>
              <div className="pm-empty-title">{searchTerm ? 'No payments found' : 'No payments yet'}</div>
              <div className="pm-empty-sub">{searchTerm ? 'Try a different search term' : 'Record a payment from Student balances when a student pays fees.'}</div>
            </div>
          </div>
        ) : groupHistoryByBatch ? (
          <div>
            {paymentsGroupedByBatch.map((group) => (
              <div key={group.batchId} className="pm-batch-group">
                <div className="pm-batch-group-head">
                  <span>{group.label}</span>
                  <span className="pm-batch-group-meta">
                    {group.payments.length} payment{group.payments.length !== 1 ? 's' : ''} · {formatRwf(group.total)}
                  </span>
                </div>
                <div className="pm-table-card">
                  <div className="pm-scroll pm-table-wrap">
                    <table className="pm-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Batch</th>
                          <th>Amount</th>
                          <th>Method</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.payments.map((p, i) => renderPaymentHistoryRow(p, i))}
                      </tbody>
                    </table>
                  </div>
                  <div className="pm-cards">
                    {group.payments.map((p, i) => renderPaymentHistoryCard(p, i))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pm-table-card">
            {/* Desktop table */}
            <div className="pm-scroll pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Batch</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.map((p, i) => renderPaymentHistoryRow(p, i))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="pm-cards">
              {paginatedPayments.map((p, i) => renderPaymentHistoryCard(p, i))}
            </div>

            <div className="pm-pagination">
              <div className="pm-pagination-info">
                Showing <strong>{historyRangeStart}–{historyRangeEnd}</strong> of <strong>{filteredPayments.length}</strong> payments
              </div>
              <div className="pm-pagination-controls">
                <select
                  className="pm-pagination-size"
                  value={historyPageSize}
                  onChange={(e) => { setHistoryPageSize(Number(e.target.value)); setHistoryPage(1); }}
                  aria-label="Rows per page"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
                <button type="button" className="pm-pagination-btn" disabled={safeHistoryPage <= 1} onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}>← Prev</button>
                <span className="pm-pagination-page">Page {safeHistoryPage} of {historyTotalPages}</span>
                <button type="button" className="pm-pagination-btn" disabled={safeHistoryPage >= historyTotalPages} onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}>Next →</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentManagement;
